import {isDevMode} from "@angular/core";
import {ExpirationDescriptor, HedgeMatrixRow, HgHedgeMatrixComponent} from "./hg-hedge-matrix.component";
import {
    CellClassParams, CellDoubleClickedEvent,
    ColDef,
    ColGroupDef, ColSpanParams, ColumnMovedEvent, DisplayedColumnsChangedEvent,
    GetContextMenuItemsParams, GridApi,
    GridOptions,
    GridReadyEvent, ICellEditorParams, ICellRendererParams,
    IsColumnFuncParams,
    MenuItemDef, RowNode, RowSpanParams,
    ValueSetterParams,
} from "ag-grid-community";
import {AgGridColumn} from "ag-grid-angular";
import * as Enumerable from "linq";
import {ValueFormatterParams, ValueGetterParams} from "ag-grid-community/dist/lib/entities/colDef";
import {centeredColumnDef} from "../../ag-grid-contrib";
import {
    defaultCurrencyFormatter,
    isValidNumber,
    isVoid,
    makeDayOfWeekDate, makeGuiFriendlyExpirationDate,
} from "../../utils";
import {HedgeData} from "./hedge-data";
import {HedgeMatrixCustomColumnHeaderGroup} from "./hedge-matrix-custom-colgroup-header";
import {HgMatrixPunchPadComponent} from "./punch-pad/hg-matrix-punch-pad.component";
import {HedgeExpirationCellEditorComponent} from "./hedge-expiration-cell-editor.component";
import {MultiExpirationCellRenderer} from "./multi-expiration-cell-renderer.component";
import {DateTime} from "luxon";
import {Subject} from "rxjs";
import {parseOptionTicker, zipOptionTicker} from "../../options-common/options.model";
import {hedgePocketStorageKeyResolver} from "./hege-pocket/hedge-pocket.component";
import {HedgeMatrixCellData} from "./hedge-matrix-cell.data";
import {ExpectedMoveArgs} from "../../expected-move/expected-move.service";
import {formatCurrency} from "@angular/common";
import {HedgeMatrixCustomHeaderComponent} from "./custom-header/hedge-matrix-custom-header.component";
import {HedgeMatrixTotalCustomHeaderComponent} from "./custom-header/hedge-matrix-total-custom-header.component";
import {ExpirationPnlCellRenderer} from "./expiration-pnl-cell-renderer.component";
import {IvDayOfWeekCellRendererComponent} from "./iv-day-of-week-cell-renderer.component";
import {
    buildHedgeColId, buildHedgeGroupId, buildOtmColId, buildPinnedGroupId,
    buildPnlExpirationColId, buildPnlExpirationGroupId, buildPnlTotalColId,
    buildPnlTotalGroupId, buildPricingColId, buildQtyTotalColId,
    buildQtyTotalGroupId, buildSectionGroupId, buildSpacerColId,
    buildStrikeColId, getHedgeIdFromColId, isHedgeColumn as isHedgeCol,
    isQtyTotalColumn, isSpacerColumn, parseColId
} from "./hedge-matrix-col-id";

const width60 = 60;
const width70 = 70;
const width80 = 80;
const width90 = 90;
const width100 = 100;
const width110 = 115;

const earmarkWidthExtra = 70;

export const Width_FitDate = 91;
export const Width_Fit3digit = 101;
export const Width_Fit6digit = 111;

// STEPPING ON A STRIKE — a pending trade reversing the book's net at a contract (CR-20260824-004).
// ⚠ The put colour is a DIFFERENT red from `getColor`'s `darkred` short colour, deliberately: on
// the put side a stepping cell and a plain short cell sit side by side and must stay tellable apart.
const SteppingColourCall = 'indigo';
const SteppingColourPut = 'red';

function getHedgeUnderlyingDisplay(comp: HgHedgeMatrixComponent, hedge: HedgeData): string | undefined {
    const section = comp.nonBaseUnderlyingSections?.find(
        s => s.hedgeList?.some(h => h.id === hedge.id)
    );
    return section?.displayName;
}

function makeEarmarkedHeaderTemplate(headerText: string, underlyingDisplay: string): object {
    if (!underlyingDisplay) {
        return undefined;
    }
    return {
        template:
            `<div class="ag-cell-label-container" role="presentation">
              <div data-ref="eLabel" class="ag-header-cell-label" role="presentation"
                   style="justify-content: center">
                <span class="underlying-badge">${underlyingDisplay}</span>
                <span data-ref="eText" class="ag-header-cell-text" role="columnheader">${headerText}</span>
              </div>
            </div>`
    };
}

export function getHedgeMatrixGridOptions(comp: HgHedgeMatrixComponent): GridOptions {

    const subj = new Subject();

    const options: GridOptions = {};

    options.defaultColDef = centeredColumnDef;

    options.columnDefs = getColumnDefs(comp);

    options.rowClass = 'ets-text-centered';

    options.suppressRowTransform = true;

    options.rowModelType = 'clientSide';

    options.rowData = [];

    options.tooltipShowDelay = 250;

    // options.applyColumnDefOrder = true;

    options.suppressDragLeaveHidesColumns = true;

    options.suppressColumnVirtualisation = true;

    options.suppressCellSelection = false;

    options.animateRows = false;
    // options.suppressAnimationFrame = true;
    // options.suppressScrollOnNewData = true;

    options.frameworkComponents = {

        // Totals header showing Grand Total (after aqdoesuisition) in a top-level grid header
        matrixTotalGroupHeader: HedgeMatrixCustomColumnHeaderGroup,

        punchPadCellEditor: HgMatrixPunchPadComponent,
        hedgeExpirationCellEditor: HedgeExpirationCellEditorComponent,
        multiExpirationCellRenderer: MultiExpirationCellRenderer,
        expirationPnlCellRenderer: ExpirationPnlCellRenderer,
        ivDayOfWeekRenderer: IvDayOfWeekCellRendererComponent,

        // Allows custom hedge names with colors
        hmCustomHeader: HedgeMatrixCustomHeaderComponent,

        // Component replaces standard header to allow copy existing positions into Maven. Offers choice "Calls/Puts/Both" popup
        hmTotalCustomHeader: HedgeMatrixTotalCustomHeaderComponent
    }

    options.getRowNodeId = (data: HedgeMatrixRow) => data.rowId;

    options.onVirtualRowRemoved = (args) => {
        subj.next();
    };

    options.onColumnMoved = (args: ColumnMovedEvent) => {
        comp.updateVisibleColumns();
    }

    options.onDisplayedColumnsChanged = (args: DisplayedColumnsChangedEvent) => {
        comp.updateVisibleColumns();
    }

    options.onGridReady = (args: GridReadyEvent) => comp.onGridReady(args);

    // CR-20260901-005: undo gesture boundary. The pre-state is captured when the edit session
    // OPENS — the punch-pad editor writes its roll/amend-other-legs side effects before the value
    // setter runs, so only a session-start snapshot holds the true "before". Stop without a commit
    // (Escape / cancel / no-op) drops it.
    options.onCellEditingStarted = () => comp.onMatrixCellEditingStarted();
    options.onCellEditingStopped = () => comp.onMatrixCellEditingStopped();

    options.getContextMenuItems = (args: GetContextMenuItemsParams) => {

        const menuItems: (string | MenuItemDef)[] = [];

        const colDef = args.column?.getColDef() || {};
        const colId = args.column?.getColId() || '';

        const hedgeId = getHedgeIdFromColId(colId);

        const hedgeData = hedgeId
            ? comp.hedgeMatrixDataService.getHedge(hedgeId)
            : undefined;

        const isHedgeColumn = isHedgeCol(colId);

        const isNewHedge = isHedgeColumn && hedgeData?.isNew;

        if (isHedgeColumn && !isNewHedge) {

            const hedgeSilo = hedgeData?.underlying || comp.hedgeMatrixDataService.getPortfolioUnderlying();
            const colGroupId = buildHedgeGroupId(hedgeSilo, hedgeData?.id);

            const colGroup = args.columnApi.getColumnGroup(colGroupId);

            if (!isVoid(colGroup)) {

                const isModificationMode = colGroup.getDisplayedChildren().length > 1;

                let text = 'Modify Hedge';

                if (isModificationMode) {
                    text = 'Exit Modification Mode';
                }

                const isExpired = comp.isHedgeExpired(hedgeData);

                const modifyItem: MenuItemDef = {
                    name: text,
                    action: () => {
                        comp.modifyHege(args.column);
                    },
                    disabled: isExpired
                };

                menuItems.push(modifyItem);

                menuItems.push('separator');
            }
        }

        if (isNewHedge) {
            menuItems.push(
                {
                    name: 'Slide Hedge',
                    action: () => comp.showSlideHedgeDialog(hedgeData, 'trans')
                },
            );
        } else if (hedgeData?.underModification) {
            menuItems.push(
                {
                    name: 'Slide Outcome',
                    action: () => comp.showSlideHedgeDialog(hedgeData, 'outcome')
                },
            );
        }

        const hpStorageKey = hedgePocketStorageKeyResolver(comp.selectedPortfolio);

        // Inherit underlying + nearest expiration from selected hedge (if right-clicked on a hedge column)
        const sourceUnderlying = hedgeData?.underlying || undefined;
        const sourceExpiration = hedgeData
            ? (comp.hedgeMatrixDataService.getHedgeExpirations(hedgeData.id) || []).sort()[0]
            : undefined;

        menuItems.push({
            name: 'Add Call',
            action: () => comp.addHedge('Call', undefined, undefined, colId, 'left', sourceUnderlying, sourceExpiration),
            subMenu: [
                { name: 'To Right...', action: () => comp.addHedge('Call', undefined, undefined, colId, 'right', sourceUnderlying, sourceExpiration) }
            ]
        });

        menuItems.push({
            name: 'Add Put',
            action: () => comp.addHedge('Put', undefined, undefined, colId, 'left', sourceUnderlying, sourceExpiration),
            subMenu: [
                { name: 'To Right...', action: () => comp.addHedge('Put', undefined, undefined, colId, 'right', sourceUnderlying, sourceExpiration) }
            ]
        });

        const hedgePocketSettings = comp.userSettingsService.getValue<{
            hedgesCall: any[],
            hedgesPut: any[]
        }>(hpStorageKey);

        if (!isVoid(hedgePocketSettings)) {

            const rowData = args.node.data as HedgeMatrixRow;

            const callTemplates = Enumerable.from(hedgePocketSettings.hedgesCall)
                .groupBy(h => h.templateName)
                .select(grp => {
                    const subMenuHedges = grp.select(hi => ({
                        name: hi.strategyName,
                        action: () => comp.addHedge('Call', hi.strategyId, rowData, colId)
                    } as MenuItemDef))
                        .orderBy(i => i.name)
                        .toArray();
                    return { name: grp.key() || '<Unknown Template>', subMenu: subMenuHedges } as MenuItemDef;
                })
                .orderBy(x => x.name)
                .toArray();

            const putTemplates = Enumerable.from(hedgePocketSettings.hedgesPut)
                .groupBy(h => h.templateName)
                .select(grp => {
                    const subMenuHedges = grp.select(hi => ({
                        name: hi.strategyName,
                        action: () => comp.addHedge('Put', hi.strategyId, rowData, colId)
                    } as MenuItemDef))
                        .orderBy(i => i.name)
                        .toArray();
                    return { name: grp.key() || '<Unknown Template>', subMenu: subMenuHedges } as MenuItemDef;
                })
                .orderBy(x => x.name)
                .toArray();

            const hedgeTemplateItem: MenuItemDef = {
                name: 'Add Template',
                subMenu: []
            };

            if (!isVoid(callTemplates)) {
                (hedgeTemplateItem.subMenu as MenuItemDef[]).push({
                    name: 'Call Templates',
                    subMenu: callTemplates
                });
            }

            if (!isVoid(putTemplates)) {
                (hedgeTemplateItem.subMenu as MenuItemDef[]).push({
                    name: 'Put Templates',
                    subMenu: putTemplates
                });
            }

            menuItems.push(hedgeTemplateItem);
        }

        if (args.column) {
            if (isSpacerColumn(args.column.getColId())) {
                menuItems.push({
                    name: 'Remove Spacer',
                    action: () => comp.removeSpacer(args.column.getColId())
                });
            }
        }

        // ── Hide P&L / Pricing column (when right-clicking on one) ──
        if (args.column) {
            const parsed = parseColId(colId);
            if (parsed.type === 'pnl:expiration') {
                menuItems.push({
                    name: 'Hide Column',
                    action: () => {
                        comp.onPnlExpirationSelectionChanged(
                            { expiration: parsed.exp, side: parsed.side as 'Call' | 'Put' },
                            false, parsed.allInclusive, parsed.silo
                        );
                    }
                });
            } else if (parsed.type === 'pnl:total') {
                menuItems.push({
                    name: 'Hide Column',
                    action: () => {
                        comp.onGrandTotalExpirationSelectionChanged({ expiration: parsed.exp }, parsed.silo);
                    }
                });
            } else if (parsed.type === 'pricing') {
                menuItems.push({
                    name: 'Hide Column',
                    action: () => {
                        comp.onExpirationSelectionChanged(
                            { expiration: parsed.exp, side: parsed.side as 'Call' | 'Put' },
                            false, parsed.silo
                        );
                    }
                });
            }
        }

        // ── Add Column... ──
        {
            const addColumnSubMenu: MenuItemDef[] = [];
            const baseSilo = comp.hedgeMatrixDataService?.getPortfolioUnderlying();

            // Resolve silo + expirations from the column being right-clicked
            const contextSilo = parseColId(colId).silo || baseSilo;
            const isNonBaseSilo = contextSilo !== baseSilo;
            const contextSection = isNonBaseSilo
                ? comp.nonBaseUnderlyingSections.find(s => s.underlying === contextSilo)
                : null;
            const contextExpirations = (contextSection?.expirationList || comp.expirationList || [])
                .map(x => x.expiration)
                .filter((v, i, a) => a.indexOf(v) === i);

            // P&L submenu
            const pnlSubMenu: MenuItemDef[] = [];
            for (const side of ['Call', 'Put'] as const) {
                const sideItems: MenuItemDef[] = [];
                contextExpirations.forEach(exp => {
                    const pnlId = buildPnlExpirationColId(contextSilo, exp, side);
                    const pnlCol = comp.theGrid?.columnApi.getColumn(pnlId);
                    if (pnlCol && !pnlCol.isVisible()) {
                        sideItems.push({
                            name: makeGuiFriendlyExpirationDate(exp),
                            action: () => {
                                comp.showAndMoveColumn(pnlId, colId, 'right');
                                comp.recalculatePnls();
                            }
                        });
                    }
                });
                if (sideItems.length > 0) {
                    pnlSubMenu.push({ name: `${side}s`, subMenu: sideItems });
                }
            }
            const totalItems: MenuItemDef[] = [];
            contextExpirations.forEach(exp => {
                const pnlTotalId = buildPnlTotalColId(contextSilo, exp);
                const pnlTotalCol = comp.theGrid?.columnApi.getColumn(pnlTotalId);
                if (pnlTotalCol && !pnlTotalCol.isVisible()) {
                    totalItems.push({
                        name: makeGuiFriendlyExpirationDate(exp),
                        action: () => {
                            comp.showAndMoveColumn(pnlTotalId, colId, 'right');
                            comp.recalculatePnls();
                        }
                    });
                }
            });
            if (totalItems.length > 0) {
                pnlSubMenu.push({ name: 'Total', subMenu: totalItems });
            }
            if (pnlSubMenu.length > 0) {
                let pnlDefaultAction: () => void;
                if (hedgeData && !hedgeData.isNew) {
                    const hedgeExpirations = comp.hedgeMatrixDataService.getHedgeExpirations(hedgeData.id) || [];
                    const hedgeSide = hedgeData.type;
                    const firstExp = hedgeExpirations[0];
                    if (firstExp) {
                        const pnlId = buildPnlExpirationColId(contextSilo, firstExp, hedgeSide);
                        const pnlCol = comp.theGrid?.columnApi.getColumn(pnlId);
                        if (pnlCol && !pnlCol.isVisible()) {
                            pnlDefaultAction = () => {
                                comp.showAndMoveColumn(pnlId, colId, 'right');
                                comp.recalculatePnls();
                            };
                        }
                    }
                }
                addColumnSubMenu.push({
                    name: 'P&L',
                    action: pnlDefaultAction || undefined,
                    subMenu: pnlSubMenu
                });
            }

            // Pricing submenu
            const pricingSubMenu: MenuItemDef[] = [];
            for (const side of ['Call', 'Put'] as const) {
                const sideItems: MenuItemDef[] = [];
                contextExpirations.forEach(exp => {
                    const pricingId = buildPricingColId(contextSilo, exp, side);
                    const pricingCol = comp.theGrid?.columnApi.getColumn(pricingId);
                    if (pricingCol && !pricingCol.isVisible()) {
                        sideItems.push({
                            name: makeGuiFriendlyExpirationDate(exp),
                            action: () => {
                                comp.showAndMoveColumn(pricingId, colId, 'left');
                                comp.subscribePricingColumn(exp, side, contextSilo);
                            }
                        });
                    }
                });
                if (sideItems.length > 0) {
                    pricingSubMenu.push({ name: `${side}s`, subMenu: sideItems });
                }
            }
            if (pricingSubMenu.length > 0) {
                let pricingDefaultAction: () => void;
                if (hedgeData && !hedgeData.isNew) {
                    const hedgeExpirations = comp.hedgeMatrixDataService.getHedgeExpirations(hedgeData.id) || [];
                    const hedgeSide = hedgeData.type;
                    const firstExp = hedgeExpirations[0];
                    if (firstExp) {
                        const pricingId = buildPricingColId(contextSilo, firstExp, hedgeSide);
                        const pricingCol = comp.theGrid?.columnApi.getColumn(pricingId);
                        if (pricingCol && !pricingCol.isVisible()) {
                            pricingDefaultAction = () => {
                                comp.showAndMoveColumn(pricingId, colId, 'left');
                                comp.subscribePricingColumn(firstExp, hedgeSide, contextSilo);
                            };
                        }
                    }
                }
                addColumnSubMenu.push({
                    name: 'Pricing',
                    action: pricingDefaultAction || undefined,
                    subMenu: pricingSubMenu
                });
            }

            // Spacer
            addColumnSubMenu.push({
                name: 'Spacer',
                action: () => comp.addSpacer(colId, 'left'),
                subMenu: [
                    { name: 'To Right...', action: () => comp.addSpacer(colId, 'right') }
                ]
            });

            if (addColumnSubMenu.length > 0) {
                menuItems.push({
                    name: 'Add Column...',
                    subMenu: addColumnSubMenu
                });
            }
        }

        const removeHedgeItem: MenuItemDef = {
            name: 'Remove Hedge',
            action: () => comp.removeHedge(hedgeData?.id),
        };

        if (hedgeData && hedgeData.isNew) {
            menuItems.push(removeHedgeItem);
        }

        const deleteHedgeItem: MenuItemDef = {
            name: 'Delete Hedge',
            action: () => comp.deleteHedge(hedgeData),
        };

        if (hedgeData && !hedgeData.isNew) {
            menuItems.push(deleteHedgeItem);
            menuItems.push('separator');
        }

        const numberOfModifiedHedges = comp.getModificationsCount();

        // Source for copy is inferred from clicked column subtype:
        // hedge:{silo}:{hedgeId}:{qty|trans|outcome}.
        const parsedClickedCol = parseColId(colId);
        const clickedSubType: 'qty' | 'trans' | 'outcome' = (parsedClickedCol?.type === 'hedge' && parsedClickedCol.hedgeSubType)
            ? parsedClickedCol.hedgeSubType
            : 'trans';

        // Trans source requires modifications. Qty / Outcome work without.
        const showCopyMenu = clickedSubType === 'trans'
            ? numberOfModifiedHedges > 0
            : true;

        if (showCopyMenu) {
            const copyOrdersMenuItem = {
                name: 'Copy Orders To...',
                subMenu: []
            };

            const otherPanelItem: any = {
                name: 'Copy To Other Panel',
                action: () => comp.copyOrders(hedgeData, 'other-panel', clickedSubType),
            };
            if (numberOfModifiedHedges > 1) {
                otherPanelItem.subMenu = [
                    {name: 'All', action: () => comp.copyAllChangesToHedgingGrid()},
                ];
            }
            copyOrdersMenuItem.subMenu.push(otherPanelItem);

            copyOrdersMenuItem.subMenu.push({
                    name: 'Copy To TOS',
                    action: () => comp.copyOrders(hedgeData, 'tos', clickedSubType)
                }
            );

            menuItems.push(copyOrdersMenuItem);
        }

        if (numberOfModifiedHedges > 0 && comp.isAcquireChangesAvailable()) {
            const acquireItem: any = {
                name: 'Acquire Changes',
                action: () => comp.acquireChanges(hedgeData),
            };
            if (numberOfModifiedHedges > 1) {
                acquireItem.subMenu = [
                    {name: 'All', action: () => comp.acquireAllChanges()},
                ];
            }
            menuItems.push(acquireItem);
        }

        menuItems.push('separator');

        menuItems.push({
            name: 'Rebuild Grid',
            action: () => comp.rebuildGrid()
        });

        menuItems.push('separator');

        if (comp.canPasteHedgeFromOpg) {
            menuItems.push({
                name: 'Paste from OPG',
                action: () => comp.pasteHedgeFromOpg()
            });
        }

        if (comp.canPasteHedgeFromPcg) {
            menuItems.push({
                name: 'Paste from PCG',
                action: () => comp.pasteHedgesFromPcg()
            });
        }

        if (comp.canPasteHedgeFromOpp) {
            menuItems.push({
                name: 'Paste from OPP',
                action: () => comp.pasteHedgeFromOpp()
            });
        }

        if (comp.canPasteHedgeFromClipboard) {
            menuItems.push({
                name: 'Paste from Clipboard',
                action: () => comp.pasteHedgeFromClipboard()
            });
        }

        menuItems.push('autoSizeAll');
        menuItems.push('sizeColumnsToFit');

        menuItems.push('separator');

        menuItems.push({
            name: 'Reset Grid State',
            action: () => comp.resetGridState()
        });

        if (isDevMode()) {
            menuItems.push('separator');
            menuItems.push({
                name: 'Dump Grid State',
                action: () => comp.dumpGridState()
            });
            menuItems.push({
                name: 'Dump Saved State',
                action: () => comp.dumpSavedGridState()
            });
        }

        return menuItems;
    }

    options.onCellDoubleClicked = (params: CellDoubleClickedEvent) => {

        if (isVoid(params.data)) {
            return;
        }

        if (!params.data.isPinned) {
            return;
        }

        const colDef = params.colDef || {};
        const dblClickColId = params.column?.getColId() || '';

        const hedgeId = getHedgeIdFromColId(dblClickColId);

        const hedge = hedgeId ? comp.hedgeMatrixDataService.getHedge(hedgeId) : undefined;

        if (hedge) {
            if (params.data.isHedgePrice) {
                let legType: any = '';

                if (hedge.isNew || colDef.headerName === 'Trans') {
                    legType = 'trans';
                } else {
                    if (colDef.headerName === 'Outcome') {
                        legType = 'outcome';
                    } else {
                        legType = 'original';
                    }
                }

                comp.showPricingSteps(hedgeId, legType);
            }
        } else {

            if (params.data.isHedgePrice) {
                return null;
            }

            const colId = params.colDef.colId;

            let side: string;
            let legType: any;

            if (params.data.isExpiration) { // original
                legType = 'original';
            } else if (params.data.isDayOfWeek) { // transaction
                legType = 'trans';
            }

            if (isQtyTotalColumn(colId)) {
                side = parseColId(colId).side;
            } else {
                if (!colId.startsWith('otm:')) {
                    console.error(`click unknown column ${colId}, pricing steps parameters cannot be determined`);
                    return null;
                }
            }

            const hasAnyTransaction = comp.hedgeMatrixDataService.hasAnyTransactions(side);

            if (hasAnyTransaction) {
                comp.showPricingSteps('total', legType, null, side);
            }
        }
    }

    options.context = {
        subj
    };

    return options;
}

export function getColumnDefs(comp: HgHedgeMatrixComponent): Partial<AgGridColumn>[] {

    const cols: Partial<AgGridColumn>[] = [];

    const pinnedColumns = getPinnedColumns(comp);

    cols.push(pinnedColumns);

    if (!comp.hedgeMatrixDataService) {
        return cols;
    }

    // Base underlying (SPX) hedges only
    const baseUnderlying = comp.hedgeMatrixDataService.getPortfolioUnderlying();
    const baseHedges = Enumerable.from(
        comp.hedgeMatrixDataService.getHedgesByUnderlying(baseUnderlying)
    );

    const expirations = comp.expirationList.map(x => {
        const column = getPricingColumn(comp, x);
        return column;
    });

    cols.push(...expirations);

    // Hedge columns — all hedges in data-service order (structural default: calls first, then puts)
    const hedgesColumns = baseHedges
        .select(h => getHedgeColumn(comp, h))
        .toArray();
    cols.push(...hedgesColumns);

    // P&L per (side, expiration)
    const pnlExpirations = comp.pnlExpirationsList.flatMap(x => {
        const column = getPnlExpirationGroup(comp, x, false);
        const allInclusive = getPnlExpirationGroup(comp, x, true);
        return [column, allInclusive];
    });
    cols.push(...pnlExpirations);

    const uniqueExpirations = Enumerable.from(comp.expirationList)
        .select(x => x.expiration)
        .distinct()
        .toArray();

    const grandTotalCols = uniqueExpirations.map(x => {
        const exp = comp.expirationList.find(y => y.expiration === x);
        const col = getPnlTotalColumn(comp, exp);
        return col;
    });

    cols.push(...grandTotalCols);

    const totals = getQtyTotalColumns(comp);

    cols.push(...totals);

    // Non-base underlying sections (e.g., ES contracts) — only when ES hedging is enabled
    if (comp.esHedgingEnabled) {
        for (const section of comp.nonBaseUnderlyingSections) {
            const esCols = getEsUnderlyingSectionColumns(comp, section);
            cols.push(...esCols);
        }
    }

    const spacer = getSpacerColumns();

    cols.push(...spacer);

    return cols;
}


/**
 * Interleaves hedge columns with P&L columns, grouped by (side, expiration).
 * Uses P&L entries as the driver: for each P&L entry, finds matching hedges
 * and places them immediately to its left.
 *
 * Layout: [Call hedges exp1] [P&L Call exp1] [P&L Call exp1 All] [Call hedges exp2] ...
 *         [Put hedges exp1] [P&L Put exp1] ...
 */
function getInterleavedHedgePnlColumns(
    comp: HgHedgeMatrixComponent,
    hedges: HedgeData[],
    pnlExpirationsList: ExpirationDescriptor[],
    pnlColumnFn?: (comp: HgHedgeMatrixComponent, exp: ExpirationDescriptor, allInclusive: boolean) => Partial<AgGridColumn>,
): Partial<AgGridColumn>[] {
    const cols: Partial<AgGridColumn>[] = [];
    const getPnlCol = pnlColumnFn || ((c, e, a) => getPnlExpirationGroup(c, e, a));
    const consumedHedgeIds = new Set<string>();

    // Deduplicate P&L entries by (side, expiration) — preserve original order (Call first, then Put, by expiration)
    const seenPnlKeys = new Set<string>();
    const uniquePnl: ExpirationDescriptor[] = [];
    for (const pnl of pnlExpirationsList) {
        const key = `${pnl.side}:${pnl.expiration}`;
        if (!seenPnlKeys.has(key)) {
            seenPnlKeys.add(key);
            uniquePnl.push(pnl);
        }
    }

    for (const pnl of uniquePnl) {
        // Find hedges matching this P&L entry's (side, expiration)
        const matchingHedges = hedges.filter(h =>
            h.type === pnl.side &&
            !consumedHedgeIds.has(h.id) &&
            comp.hedgeMatrixDataService.getHedgeExpirations(h.id).includes(pnl.expiration)
        );

        for (const h of matchingHedges) {
            cols.push(getHedgeColumn(comp, h));
            consumedHedgeIds.add(h.id);
        }

        // Push P&L columns (normal + all-inclusive)
        cols.push(getPnlCol(comp, pnl, false));
        cols.push(getPnlCol(comp, pnl, true));
    }

    // Any remaining hedges not matched to any P&L entry (e.g., new hedges with no cell data)
    for (const h of hedges) {
        if (!consumedHedgeIds.has(h.id)) {
            cols.push(getHedgeColumn(comp, h));
        }
    }

    return cols;
}

function getPinnedColumns(comp: HgHedgeMatrixComponent): ColGroupDef {
    const otmCol = getOtmColumn(comp);
    const strikeCol = getStrikeColumn(comp);

    const group: ColGroupDef = {
        groupId: buildPinnedGroupId(),
        headerGroupComponent: 'matrixTotalGroupHeader',
        headerGroupComponentParams: {
            context: {
                comp,
                type: 'master',
                side: undefined,
                expiration: undefined,
                allInclusive: undefined
            },
        },
        children: [otmCol, strikeCol],
    }

    return group;
}

function getStrikeColumn(comp: HgHedgeMatrixComponent): ColDef {
    const baseSilo = comp.hedgeMatrixDataService?.getPortfolioUnderlying() || 'SPX';
    const col: ColDef = {
        headerName: 'Strike',
        pinned: true,
        field: 'strike',
        colId: buildStrikeColId(baseSilo),
        minWidth: width80,
        width: width80,
        headerClass: 'ets-text-centered',
        sortable: false,
        sortIndex: 0,
        sort: 'desc',
        rowSpan: (params: RowSpanParams) => params.data.rowSpan || 1,
        cellStyle(args: CellClassParams) {

            if (!args.data) {
                return undefined;
            }

            if (args.data.isPinned) {
                return {
                    background: '#2a2a2a',
                    color: 'gold',
                    'font-weight': 'bold',
                };
            }

            const styleObj = {
                color: '#fff',
                'font-weight': '800',
                background: 'inherit',
                border: 'none',
            };

            const bgColors = [];

            const atmStrike = comp.getAtmStrike();

            if (args.data.strike === atmStrike) {
                bgColors.push('#fff');
            }

            let portfolioLegColors = comp.hedgeMatrixDataService
                .getPortfolioLegColorByStrike(args.data.strike);

            if (comp.showPortfolioPositions) {
                if (portfolioLegColors.length > 0) {
                    bgColors.push(...portfolioLegColors);
                }
            }

            if (args.data && (args.data.strike === comp.centerStrike)) {
                bgColors.push('black');
            }

            const rowData = args.data as HedgeMatrixRow;

            if (rowData.rowSpan > 1) {
                styleObj['display'] = 'flex';
                styleObj['align-items'] = 'center';
                styleObj['justify-content'] = 'center';
                styleObj['border-bottom-color'] = 'rgb(66, 66, 66)';
            }


            if (bgColors.length === 0) {
                if (rowData.rowSpan > 1) {
                    styleObj['background'] = '#2a2a2a';
                }
                return styleObj;
            }

            let background;

            if (bgColors.length === 1) {
                background = bgColors[0];
            } else if (bgColors.length === 2) {
                background = `linear-gradient(90deg, ${bgColors[0]} 0% 50%, ${bgColors[1]} 50% 100%)`;
            } else if (bgColors.length === 3) {
                background = `linear-gradient(90deg, ${bgColors[0]} 0% 33%, ${bgColors[1]} 33% 66%, ${bgColors[2]} 66%)`;
            } else if (bgColors.length === 4) {
                background = `linear-gradient(90deg, ${bgColors[0]} 0% 25%, ${bgColors[1]} 25% 50%, ${bgColors[2]} 50% 75%, ${bgColors[3]} 75%)`;
            }


            styleObj['background'] = background;

            if (bgColors.indexOf('#fff') >= 0) {
                styleObj['color'] = 'black';
            }

            if (bgColors.indexOf('black') >= 0) {
                if (bgColors.indexOf('orange') === -1) {
                    styleObj['color'] = 'gold';
                }
            }

            // CR-20260501-001 H1c — removed self-perpetuating
            // setTimeout(() => refreshCells({force:true, rowNodes:[args.node]}), 50)
            // here. It accounted for ~88% of the 50ms timer storm seen in
            // the 2026-04-26 trace (cellStyle scheduling refreshCells which
            // re-invoked cellStyle which scheduled another, self-paced at
            // 50ms per gradient cell). Original intent: force gradient
            // background to paint on first render. If gradient regresses,
            // restore via H1's existing rAF-coalesced subj.next() pipeline
            // (gridOptions.context.subj) instead of a per-cellStyle setTimeout.

            return styleObj;
        }
    }

    return col;
}

export function getHedgeColumn(comp: HgHedgeMatrixComponent, hedge: HedgeData) {
    if (hedge.isNew) {
        return getNewHedgeColumn(comp, hedge);
    } else {
        return getExistingHedgeColumn(comp, hedge);
    }
}

function getExistingHedgeColumn(comp: HgHedgeMatrixComponent, hedge: HedgeData) {

    const ulDisplay = getHedgeUnderlyingDisplay(comp, hedge);
    const extraWidth = ulDisplay ? earmarkWidthExtra - 10 : 0;
    const hedgeSilo = hedge.underlying || comp.hedgeMatrixDataService.getPortfolioUnderlying();

    const colDef: ColGroupDef = {
        headerName: hedge.type.toUpperCase() + 'S',
        marryChildren: true,
        groupId: buildHedgeGroupId(hedgeSilo, hedge.id),
        children: [
            {
                headerName: hedge.name,
                colId: buildHedgeColId(hedgeSilo, hedge.id, 'qty'),
                minWidth: Width_FitDate + extraWidth,
                width: width100 + extraWidth,
                sortable: false,
                cellStyle: getHedgeColumnCellStyle(comp),
                cellRendererSelector: (params: ICellRendererParams) => {
                    const isMulti = !params.data.isPinned
                        && comp.hedgeMatrixDataService.doesHaveMultipleExpirations(hedge.id);

                    if (isMulti) {
                        return {
                            component: 'multiExpirationCellRenderer',
                            params: {
                                etsComponent: comp,
                                hedge,
                                field: 'qty'
                            }
                        }
                    }

                    return null;
                },
                headerComponent: 'hmCustomHeader',
                headerComponentParams: {
                    context: {
                        etsComponent: comp,
                        hedge: hedge,
                        underlyingDisplay: getHedgeUnderlyingDisplay(comp, hedge),
                    }
                },
                headerClass: 'ets-ag-header-center',
                valueGetter: (params: ValueGetterParams) => {
                    const strike = params.data.strike;

                    if (params.data.isPinned) {
                        if (params.data.isExpiration || params.data.isDayOfWeek) {
                            const val = params.data.isExpiration
                                ? getHedgeExpirationsString(comp, hedge, 'original')
                                : getDaysOfWeekString(comp, hedge, 'original')
                            return val;
                        } else if (params.data.isHedgePrice) {
                            return comp.getHedgePriceFormatted(hedge)
                        } else {
                            return undefined;
                        }
                    }

                    if (isVoid(strike)) {
                        return undefined;
                    }

                    const cellData = comp.hedgeMatrixDataService.getCellData(
                        strike,
                        hedge.id,
                    );

                    if (isVoid(cellData)) {
                        return null;
                    }

                    const expirationCellDataIndex = getExpirationCellDataIndex(
                        strike,
                        params.api,
                        params.node
                    );
                    const expirationCellData = cellData[expirationCellDataIndex];

                    return expirationCellData?.qty;
                },
                valueFormatter: getValueFormatterForHedgeColumn(comp, hedge),
            },
            {
                headerName: 'Trans',
                colId: buildHedgeColId(hedgeSilo, hedge.id, 'trans'),
                field: 'transQty',
                headerClass: 'opg-difference',
                minWidth: Width_FitDate + extraWidth,
                width: width100 + extraWidth,
                hide: true,
                sortable: false,
                cellRendererSelector: (params: ICellRendererParams) => {
                    const isMulti = !params.data.isPinned
                        && comp.hedgeMatrixDataService.doesHaveMultipleExpirations(hedge.id);

                    if (isMulti) {
                        return {
                            component: 'multiExpirationCellRenderer',
                            params: {
                                etsComponent: comp,
                                hedge,
                                field: 'transQty'
                            }
                        }
                    }

                    return null;
                },
                cellEditorSelector: (params: ICellEditorParams) => {
                    const isMouseClick = isVoid(params.charPress) && isVoid(params.keyPress);
                    if (isMouseClick) {
                        if (params.data.isPinned) {
                            if (params.data.isExpiration) {
                                return {component: 'hedgeExpirationCellEditor'}
                            }
                        }
                        return {component: 'punchPadCellEditor'};
                    }
                },
                cellEditorParams: {
                    etsComponent: comp,
                    fieldEdited: 'transQty'
                },
                cellStyle: getModifierColumnCellStyle(comp, 'transQty'),
                editable: (params: IsColumnFuncParams) => !params.data.isPinned,
                valueGetter: (params: ValueGetterParams) => {
                    const strike = params.data.strike;

                    if (!isValidNumber(strike, true)) {
                        if (params.data.isPinned) {
                            if (params.data.isHedgePrice) {
                                const transCost = comp.hedgeMatrixDataService.getTransCost(
                                    hedge.id
                                );
                                return transCost;
                            } else if (params.data.isExpiration || params.data.isDayOfWeek) {
                                const val = params.data.isExpiration
                                    ? getHedgeExpirationsString(comp, hedge, 'trans')
                                    : getDaysOfWeekString(comp, hedge, 'trans')
                                return val;
                            }
                        }
                        return null;
                    }

                    const cellData = comp.hedgeMatrixDataService.getQty(strike, hedge.id);

                    if (isVoid(cellData)) {
                        return null;
                    }

                    const expirationCellDataIndex = getExpirationCellDataIndex(
                        strike,
                        params.api,
                        params.node
                    );
                    const expirationCellData = cellData[expirationCellDataIndex];
                    const transQty = expirationCellData?.transQty;
                    return transQty;
                },
                valueSetter: getQtyValueSetter(comp, hedge, 'transQty'),
                valueFormatter: getValueFormatterForHedgeColumn(comp, hedge)
            },
            {
                headerName: 'Outcome',
                colId: buildHedgeColId(hedgeSilo, hedge.id, 'outcome'),
                field: 'outcomeQty',
                headerClass: 'opg-difference',
                minWidth: Width_FitDate + extraWidth,
                width: width100 + extraWidth,
                hide: true,
                sortable: false,
                cellRendererSelector: (params: ICellRendererParams) => {
                    const isMulti = !params.data.isPinned
                        && comp.hedgeMatrixDataService.doesHaveMultipleExpirations(hedge.id);

                    if (isMulti) {
                        return {
                            component: 'multiExpirationCellRenderer',
                            params: {
                                etsComponent: comp,
                                hedge,
                                field: 'outcomeQty'
                            }
                        }
                    }

                    return null;
                },
                cellEditorSelector: (params: ICellEditorParams) => {
                    const isMouseClick = isVoid(params.charPress) && isVoid(params.keyPress);
                    if (isMouseClick) {
                        return {
                            component: 'punchPadCellEditor',
                        };
                    }
                },
                cellEditorParams: {
                    etsComponent: comp,
                    fieldEdited: 'outcomeQty'
                },
                cellStyle: getModifierColumnCellStyle(comp, 'outcomeQty'),
                editable: (params: IsColumnFuncParams) => !params.data.isPinned,
                valueGetter: (params: ValueGetterParams) => {
                    const strike = params.data.strike;
                    if (!isValidNumber(strike, true)) {
                        if (params.data.isPinned) {
                            if (params.data.isHedgePrice) {
                                const transCost = comp.hedgeMatrixDataService
                                    .getOutcomeCost(hedge.id);
                                return transCost;
                            } else if (params.data.isExpiration || params.data.isDayOfWeek) {
                                const val = params.data.isExpiration
                                    ? getHedgeExpirationsString(comp, hedge, 'outcome')
                                    : getDaysOfWeekString(comp, hedge, 'outcome')
                                return val;
                            }
                        }
                        return null;
                    }

                    const cellData = comp.hedgeMatrixDataService.getCellData(
                        strike,
                        hedge.id
                    );

                    if (isVoid(cellData)) {
                        return null;
                    }

                    const expirationCellDataIndex = getExpirationCellDataIndex(
                        strike,
                        params.api,
                        params.node
                    );

                    const expirationCellData = cellData[expirationCellDataIndex];

                    const outcomeQty = expirationCellData?.outcomeQty;

                    return outcomeQty;
                },
                valueSetter: getQtyValueSetter(comp, hedge, 'outcomeQty'),
                valueFormatter: getValueFormatterForHedgeColumn(comp, hedge)
            }
        ]
    };


    return colDef;
}

function getNewHedgeColumn(comp: HgHedgeMatrixComponent, hedge: HedgeData) {

    const ulDisplay = getHedgeUnderlyingDisplay(comp, hedge);
    const extraWidth = ulDisplay ? earmarkWidthExtra : 0;
    const hedgeSilo = hedge.underlying || comp.hedgeMatrixDataService.getPortfolioUnderlying();

    const colDef: ColGroupDef = {
        headerName: hedge.type.toUpperCase() + 'S',
        marryChildren: true,
        groupId: buildHedgeGroupId(hedgeSilo, hedge.id),
        children: [
            {
                headerName: hedge.name,
                colId: buildHedgeColId(hedgeSilo, hedge.id, 'qty'),
                minWidth: width90 + extraWidth,
                width: width100 + extraWidth,
                sortable: false,
                cellStyle: getModifierColumnCellStyle(comp, 'transQty'),
                headerComponent: 'hmCustomHeader',
                headerComponentParams: {
                    context: {
                        etsComponent: comp,
                        hedge: hedge,
                        underlyingDisplay: ulDisplay,
                    }
                },
                headerClass: 'ets-ag-header-center',
                cellRendererSelector: (params: ICellRendererParams) => {
                    const isMulti = !params.data.isPinned
                        && comp.hedgeMatrixDataService.doesHaveMultipleExpirations(hedge.id);

                    if (isMulti) {
                        return {
                            component: 'multiExpirationCellRenderer',
                            params: {
                                etsComponent: comp,
                                hedge,
                                field: 'transQty'
                            }
                        }
                    }
                },
                cellEditorSelector: (params: ICellEditorParams) => {
                    const isMouseClick = isVoid(params.charPress) && isVoid(params.keyPress);
                    if (isMouseClick) {
                        if (params.data.isPinned) {
                            return {component: 'hedgeExpirationCellEditor'}
                        }
                        return {component: 'punchPadCellEditor'};
                    }
                },
                cellEditorParams: {
                    api: comp.theGrid,
                    etsComponent: comp,
                    hedgeId: hedge.id,
                    fieldEdited: 'transQty'
                },
                editable: (params: IsColumnFuncParams) => !params.data.isPinned || params.data.isExpiration || params.data.isDayOfWeek,
                valueGetter: (params: ValueGetterParams) => {
                    const strike = params.data.strike;

                    if (!isValidNumber(strike, true)) {
                        if (params.data.isPinned) {
                            if (params.data.isHedgePrice) {
                                const transCost = comp.hedgeMatrixDataService
                                    .getTransCost(hedge.id);
                                return transCost;
                            } else if (params.data.isExpiration || params.data.isDayOfWeek) {
                                const val = params.data.isExpiration
                                    ? getHedgeExpirationsString(comp, hedge, 'trans')
                                    : getDaysOfWeekString(comp, hedge, 'trans')
                                return val;
                            }
                        }
                        return null;
                    }

                    const cellData = comp.hedgeMatrixDataService.getCellData(
                        strike,
                        hedge.id
                    );

                    if (isVoid(cellData)) {
                        return null;
                    }

                    const expirationCellDataIndex = getExpirationCellDataIndex(
                        strike,
                        params.api,
                        params.node
                    );

                    const expirationCellData = cellData[expirationCellDataIndex];

                    if (isVoid(expirationCellData)) {
                        return null;
                    }

                    const transQty = expirationCellData?.transQty;

                    return transQty;
                },
                valueSetter: getQtyValueSetter(comp, hedge, 'transQty'),
                valueFormatter: getValueFormatterForHedgeColumn(comp, hedge)
            },
        ]
    };


    return colDef;
}

function getPricingColumn(comp: HgHedgeMatrixComponent, expiration: ExpirationDescriptor): ColDef {
    const baseSilo = comp.hedgeMatrixDataService?.getPortfolioUnderlying() || 'SPX';
    const columnId = buildPricingColId(baseSilo, expiration.expiration, expiration.side);

    const theColumn: ColDef = {
        headerName: `${expiration.side.toUpperCase()}S`,
        colId: columnId,
        minWidth: width90,
        maxWidth: width90,
        headerClass: 'ets-text-centered',
        hide: true,
        sortable: false,
        rowSpan: (params: RowSpanParams) => params.data.rowSpan || 1,
        valueGetter: (params: ValueGetterParams) => {
            if (params.data.isPinned) {
                if (params.data.isExpiration || params.data.isDayOfWeek) {
                    const val = params.data.isExpiration
                        ? expiration.expirationFriendly
                        : expiration.dayOfWeek
                    return val;
                } else {
                    return undefined;
                }
            }

            const ticker = comp.hedgeMatrixDataService.getTickerForCell(
                params.data.strike,
                expiration.expiration,
                expiration.side
            );
            const lastQuote = comp.lastQuoteCache.getLastQuote(ticker);
            return lastQuote?.mid;
        },
        valueFormatter: (params: ValueFormatterParams) => {
            if (!isValidNumber(params.value, true)) {
                return null;
            }
            return defaultCurrencyFormatter(params.value);
        },
        cellStyle: getExpirationColumnCellStyle(comp, expiration, 'Expiration')
    };


    return theColumn;
}

function getQtyTotalColumns(comp: HgHedgeMatrixComponent, underlyingDisplay?: string): ColDef[] {
    const totalCalls: ColDef = getQtyTotalColumn(comp, 'Call', underlyingDisplay);
    const totalPuts: ColDef = getQtyTotalColumn(comp, 'Put', underlyingDisplay);
    return [totalCalls, totalPuts];
}

function getQtyTotalColumn(comp: HgHedgeMatrixComponent, side: 'Call' | 'Put', silo?: string, underlyingDisplay?: string): ColDef {

    const resolvedSilo = silo || comp.hedgeMatrixDataService?.getPortfolioUnderlying() || 'SPX';
    const headerText = `TOTAL ${side.toUpperCase()}S`;
    const earmark = makeEarmarkedHeaderTemplate(headerText, underlyingDisplay);

    const totalColumn: ColDef = {
        headerName: headerText,
        ...(earmark ? { headerComponentParams: earmark } : {}),
        minWidth: width100,
        width: width100,
        sortable: false,
        colId: buildQtyTotalColId(resolvedSilo, side),
        cellStyle: getQtyTotalColumnCellStyle(comp, side),
        rowSpan: (params: RowSpanParams) => params.data.rowSpan || 1,
        valueGetter: (params: ValueGetterParams) => {

            const rowData = params.data as HedgeMatrixRow;

            if (isVoid(rowData)) {
                return null;
            }

            const visibleHedges = comp.getSelectedHedgesIds(side);

            const uniqueExpirations = Enumerable.from(visibleHedges)
                .selectMany(x =>
                    comp.hedgeMatrixDataService.getHedgeExpirations(x))
                .distinct()
                .toArray();

            const hasMixed = uniqueExpirations.length > 1;

            if (hasMixed && !rowData.isPinned) {
                return null;
            }


            if (rowData.isPinned) {
                let value: number;

                const totalOriginalCost = comp.hedgeMatrixDataService.getTotalOriginalCost(side, visibleHedges);
                const totalTransCost = comp.hedgeMatrixDataService.getTotalTransCost(side, visibleHedges);

                if (rowData.isExpiration) {
                    if (isValidNumber(totalTransCost)) {
                        value = totalOriginalCost;
                    }

                } else if (rowData.isDayOfWeek) {
                    value = totalTransCost;

                }

                return value;
            }


            const strike = params.data.strike;

            const totalQty = comp.hedgeMatrixDataService.getTotalQtyForStrike(
                strike,
                side,
                visibleHedges
            );

            if (isVoid(totalQty)) {
                return null;
            }

            return totalQty;
        },
        valueFormatter: (params: ValueFormatterParams) => {
            const value = params.value;

            if (!isValidNumber(value)) {
                return null;
            }

            const rowData = params.data as HedgeMatrixRow;

            if (rowData && rowData.isPinned) {
                return defaultCurrencyFormatter(value);
            }

            let ticker;

            const baseUl = comp.hedgeMatrixDataService.getPortfolioUnderlying();
            const hedgeDatum = comp.hedgeMatrixDataService.getHedges()
                .filter(x => x.type === side && !isVoid(x.legs)
                    && (!x.underlying || x.underlying === baseUl))[0];

            if (hedgeDatum) {
                let hedgePosition = Enumerable.from(hedgeDatum.legs)
                    .firstOrDefault(x => !isVoid(x.ticker));

                if (!isVoid(hedgePosition)) {
                    const optionTicker = parseOptionTicker(hedgePosition.ticker);
                    optionTicker.strike = rowData.strike;
                    ticker = zipOptionTicker(optionTicker);
                }
            }

            const lastQuote = comp.lastQuoteCache.getLastQuote(ticker);

            if (isVoid(lastQuote)) {
                return value;
            }

            let sign = '';
            if (value > 0) {
                sign = '+';
            }

            const quote = defaultCurrencyFormatter(lastQuote.mid)
            const v = `${quote} (${sign}${value}) `;

            return v;
        },
    };

    const baseUl = comp.hedgeMatrixDataService?.getPortfolioUnderlying();
    const baseHedgeIds = baseUl
        ? comp.hedgeMatrixDataService.getHedgesByUnderlying(baseUl).map(h => h.id)
        : undefined;

    const grp: ColGroupDef = {
        headerGroupComponent: 'matrixTotalGroupHeader',
        headerGroupComponentParams: {
            context: {
                comp,
                type: 'side',
                side: side,
                expiration: undefined,
                allInclusive: undefined,
                hedgeIds: baseHedgeIds,
            },
        },
        groupId: buildQtyTotalGroupId(resolvedSilo, side),
        children: [totalColumn]
    }

    return grp;
}

function getOtmColumn(comp: HgHedgeMatrixComponent): ColDef {
    const baseSilo = comp.hedgeMatrixDataService?.getPortfolioUnderlying() || 'SPX';
    const col: ColDef = {
        headerName: '$ OTM',
        pinned: true,
        field: 'otm',
        colId: buildOtmColId(baseSilo),
        minWidth: width70,
        maxWidth: width70,
        sortable: false,
        headerClass: 'ets-text-centered',

        rowSpan: (params: RowSpanParams) => params.data.rowSpan || 1,

        colSpan: (params: ColSpanParams) => {
            return (params.data.isPinned && !params.data.isHedgePrice) ? 2 : 1;
        },

        cellStyle: (params: CellClassParams) => {
            if (params.data.isPinned) {
                const style = {
                    background: '#2a2a2a',
                    'font-weight': 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                };

                let color = undefined;

                if (params.value > 0) {
                    color = 'green';
                } else if (params.value < 0) {
                    color = 'red';
                }

                style['color'] = color;

                return style;
            }

            return {
                background: params.data.rowSpan > 1 ? '#2a2a2a' : undefined,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderBottomColor: 'rgb(66, 66, 66)',
            }
        },

        valueGetter: (params: ValueGetterParams) => {
            if (!params.data.isPinned) {
                return params.data.otm;
            }

            let value: number;

            const baseUl = comp.hedgeMatrixDataService.getPortfolioUnderlying();
            const visibleIds = comp.getVisibleHedges();
            const baseHedgeIds = comp.hedgeMatrixDataService.getHedgesByUnderlying(baseUl)
                .map(h => h.id)
                .filter(id => visibleIds.indexOf(id) >= 0);

            const totalOriginalCost = comp.hedgeMatrixDataService.getTotalOriginalCost(undefined, baseHedgeIds);
            const totalTransCost = comp.hedgeMatrixDataService.getTotalTransCost(undefined, baseHedgeIds);

            if (params.data.isExpiration) {
                if (isValidNumber(totalTransCost)) {
                    value = totalOriginalCost;
                }
            }

            if (params.data.isDayOfWeek) {
                value = totalTransCost;
            }

            return value;
        },

        valueFormatter: (params: ValueFormatterParams) => {

            if (isVoid(params.value)) {
                return null;
            }

            if (!params.data.isPinned) {
                return params.value;
            }

            if (!isValidNumber(params.value)) {
                return null;
            }

            const value = defaultCurrencyFormatter(params.value);
            return value;
        }
    }
    return col;
}


//
// Pnl expirations
//
function getPnlExpirationGroup(comp: HgHedgeMatrixComponent, expiration: ExpirationDescriptor, allInclusive: boolean, silo?: string, underlyingDisplay?: string) {
    const resolvedSilo = silo || comp.hedgeMatrixDataService?.getPortfolioUnderlying() || 'SPX';
    const col = getPnlExpirationColumn(comp, expiration, allInclusive, resolvedSilo, underlyingDisplay);

    const groupId = buildPnlExpirationGroupId(resolvedSilo, expiration.expiration, expiration.side, allInclusive);

    const baseUl = comp.hedgeMatrixDataService?.getPortfolioUnderlying();
    const baseHedgeIds = baseUl
        ? comp.hedgeMatrixDataService.getHedgesByUnderlying(baseUl).map(h => h.id)
        : undefined;

    const grp: ColGroupDef = {
        headerGroupComponent: 'matrixTotalGroupHeader',
        headerGroupComponentParams: {
            context: {
                comp,
                type: 'expiration-side',
                expiration: expiration.expiration,
                side: expiration.side,
                allInclusive: true,
                hedgeIds: baseHedgeIds,
            },
        },
        groupId: groupId,
        children: [col]
    }

    return grp;
}

function getPnlExpirationColumn(comp: HgHedgeMatrixComponent, expiration: ExpirationDescriptor, allInclusive: boolean, silo?: string, underlyingDisplay?: string): ColDef {

    const resolvedSilo = silo || comp.hedgeMatrixDataService?.getPortfolioUnderlying() || 'SPX';
    const header = allInclusive
        ? `P&L ${expiration.side.toUpperCase()}S (All)`
        : `P&L ${expiration.side.toUpperCase()}S`;

    const colId = buildPnlExpirationColId(resolvedSilo, expiration.expiration, expiration.side, allInclusive);

    const earmark = makeEarmarkedHeaderTemplate(header, underlyingDisplay);

    const col: ColDef = {
        headerName: header,
        ...(earmark ? { headerComponentParams: earmark } : {}),
        colId: colId,
        minWidth: width100,
        width: width100,
        headerClass: 'ets-text-centered',
        sortable: false,
        hide: true,
        cellRendererSelector: (params: ICellRendererParams) => {
            const isStrikeRow = !params.data.isPinned;

            if (isStrikeRow) {
                return null;
            }

            const isHedgePrice = params.data.isHedgePrice;

            if (!isHedgePrice) {
                return null;
            }

            return {
                component: 'expirationPnlCellRenderer',
                params: {
                    comp,
                    expiration: expiration.expiration,
                    side: expiration.side,
                    allInclusive,
                    underlying: comp.hedgeMatrixDataService.getPortfolioUnderlying(),
                }
            }
        },
        rowSpan: (params: RowSpanParams) => params.data.rowSpan || 1,
        valueGetter: (params: ValueGetterParams) => {
            if (params.data.isPinned) {
                if (params.data.isExpiration || params.data.isDayOfWeek) {
                    const val = params.data.isExpiration
                        ? expiration.expirationFriendly
                        : expiration.dayOfWeek
                    return val;
                } else if (params.data.isHedgePrice) {

                    const baseUl = comp.hedgeMatrixDataService.getPortfolioUnderlying();
                    const baseHedges = comp.hedgeMatrixDataService.getHedgesByUnderlying(baseUl);

                    let visible = allInclusive
                        ? baseHedges.map(x => x.id)
                        : comp.getVisibleHedges().filter(
                            id => baseHedges.some(h => h.id === id)
                        );

                    const hedgeIds = baseHedges
                        .filter(x => visible.indexOf(x.id) >= 0)
                        .filter(x => x.type === expiration.side)
                        .filter(x =>
                            comp.hedgeMatrixDataService.hedgeContainsExpiration(x.id, expiration.expiration));

                    const pnls = hedgeIds.map(x => {

                        const originalCost = comp.hedgeMatrixDataService.getOriginalCost(
                            x.id,
                            expiration.expiration
                        ) || 0;

                        const transCost = comp.hedgeMatrixDataService.getTransCostAsOwned(
                            x.id,
                            expiration.expiration
                        ) || 0;

                        const sum = originalCost + transCost;

                        return sum;
                    });

                    const sum = pnls.reduce((previousValue, currentValue) => previousValue + currentValue, 0);
                    return sum;
                } else {
                    return undefined;
                }
            }

            const baseUl2 = comp.hedgeMatrixDataService.getPortfolioUnderlying();
            const baseHedges2 = comp.hedgeMatrixDataService.getHedgesByUnderlying(baseUl2);

            let visible = allInclusive
                ? baseHedges2.map(x => x.id)
                : comp.getVisibleHedges().filter(
                    id => baseHedges2.some(h => h.id === id)
                );

            const pnl = comp.hedgeMatrixDataService.getExpirationPnl(
                expiration.expiration,
                params.data.strike,
                expiration.side,
                visible
            );

            return pnl;
        },
        valueFormatter: (params: ValueFormatterParams) => {
            if (!isValidNumber(params.value, true)) {
                return null;
            }

            if (params.data.isPinned) {
                return defaultCurrencyFormatter(params.value);
            } else {
                return formatCurrency(params.value, 'en-US', '$', 'USD', '1.0-0');
            }

        },
        cellStyle: getExpirationColumnCellStyle(comp, expiration, 'PnL')
    }

    return col;
}

function getPnlTotalColumn(comp: HgHedgeMatrixComponent, expiration: ExpirationDescriptor, silo?: string, underlyingDisplay?: string) {
    const resolvedSilo = silo || comp.hedgeMatrixDataService?.getPortfolioUnderlying() || 'SPX';
    const colId = buildPnlTotalColId(resolvedSilo, expiration.expiration);
    const col: ColDef = {
        headerName: `P&L TOTAL`,
        colId: colId,
        minWidth: width100,
        width: width100,
        headerClass: 'ets-ag-header-center',
        hide: true,
        sortable: false,
        headerComponent: 'hmTotalCustomHeader',
        headerComponentParams: {
            context: {
                comp,
                expiration: expiration.expiration,
                underlyingDisplay,
            },
        },
        cellRendererSelector: (params: ICellRendererParams) => {
            const isStrikeRow = !params.data.isPinned;

            if (isStrikeRow) {
                return null;
            }

            if (params.data.isDayOfWeek) {
                return {
                    component: 'ivDayOfWeekRenderer',
                    params: {
                        comp,
                        expiration: expiration.expiration,
                        dayOfWeek: expiration.dayOfWeek,
                    }
                };
            }

            const isHedgePrice = params.data.isHedgePrice;

            if (!isHedgePrice) {
                return null;
            }

            return {
                component: 'expirationPnlCellRenderer',
                params: {
                    comp,
                    expiration: expiration.expiration,
                    side: null,
                    allInclusive: false,
                    underlying: comp.hedgeMatrixDataService.getPortfolioUnderlying(),
                }
            }
        },
        rowSpan: (params: RowSpanParams) => params.data.rowSpan || 1,
        valueGetter: (params: ValueGetterParams) => {
            if (params.data.isPinned) {
                if (params.data.isExpiration || params.data.isDayOfWeek) {
                    const val = params.data.isExpiration
                        ? expiration.expirationFriendly
                        : expiration.dayOfWeek
                    return val;
                } else if (params.data.isHedgePrice) {

                    const gtBaseUl = comp.hedgeMatrixDataService.getPortfolioUnderlying();
                    const gtBaseHedges = comp.hedgeMatrixDataService.getHedgesByUnderlying(gtBaseUl);
                    let visible = comp.getVisibleHedges().filter(
                        id => gtBaseHedges.some(h => h.id === id)
                    );

                    const hedgeIds = gtBaseHedges
                        .filter(x => visible.indexOf(x.id) >= 0)
                        .filter(x =>
                            comp.hedgeMatrixDataService.hedgeContainsExpiration(
                                x.id,
                                expiration.expiration)
                        );

                    const pnls = hedgeIds.map(x => {

                        const originalCost = comp.hedgeMatrixDataService
                            .getOriginalCost(
                                x.id,
                                expiration.expiration
                            ) || 0;

                        const transCost = comp.hedgeMatrixDataService.getTransCostAsOwned(
                            x.id,
                            expiration.expiration
                        ) || 0;

                        const sum = originalCost + transCost;

                        return sum;
                    });

                    const sum = pnls.reduce((previousValue, currentValue) => previousValue + currentValue, 0);

                    return sum;

                } else {
                    return undefined;
                }
            }

            const gtBaseUl2 = comp.hedgeMatrixDataService.getPortfolioUnderlying();
            const gtBaseHedges2 = comp.hedgeMatrixDataService.getHedgesByUnderlying(gtBaseUl2);
            const visible = comp.getVisibleHedges().filter(
                id => gtBaseHedges2.some(h => h.id === id)
            );

            const callPnl = comp.hedgeMatrixDataService.getExpirationPnl(
                expiration.expiration,
                params.data.strike,
                'Call',
                visible
            );

            const putPnl = comp.hedgeMatrixDataService.getExpirationPnl(
                expiration.expiration,
                params.data.strike,
                'Put',
                visible
            );

            let grandPnl = callPnl + putPnl;

            if (comp.expirationPnlShowNormalized.includes(expiration.expiration)) {
                grandPnl = grandPnl / 100 / comp.portfolioDefaultQty;
            }

            return grandPnl;
        },
        valueFormatter: (params: ValueFormatterParams) => {
            if (!isValidNumber(params.value, true)) {
                return null;
            }

            if (params.data.isPinned) {
                return defaultCurrencyFormatter(params.value);
            } else {
                return formatCurrency(params.value, 'en-US', '$', 'USD', '1.0-0');
            }
        },
        cellStyle: getExpirationColumnCellStyle(comp, expiration, 'PnL')
    }

    const baseUl = comp.hedgeMatrixDataService?.getPortfolioUnderlying();
    const baseHedgeIds = baseUl
        ? comp.hedgeMatrixDataService.getHedgesByUnderlying(baseUl).map(h => h.id)
        : undefined;

    const grp: ColGroupDef = {
        headerGroupComponent: 'matrixTotalGroupHeader',
        headerGroupComponentParams: {
            context: {
                comp,
                type: 'expiration',
                expiration: expiration.expiration,
                side: undefined,
                allInclusive: undefined,
                hedgeIds: baseHedgeIds,
            },
        },
        groupId: buildPnlTotalGroupId(resolvedSilo, expiration.expiration),
        children: [col]
    }

    return grp;
}

function getSpacerColumns(): ColDef[] {

    const spacers = Enumerable.range(1, 6)
        .select(x => {
            const col: ColDef = {
                headerName: '< SPACER >',
                colId: buildSpacerColId(x),
                enableRowGroup: false,
                showRowGroup: true,
                lockVisible: true,
                width: width90,
                minWidth: width60,
                hide: x !== 1,
                cellStyle: (args: CellClassParams) => {

                    const styleObj = {};

                    styleObj['font-weight'] = 'normal';
                    styleObj['background-color'] = 'rgba(0,0,0,0)';
                    styleObj['color'] = undefined;
                    styleObj['border-right-color'] = 'rgb(66, 66, 66)';

                    if (args.data && args.data.isPinned) {
                        styleObj['background-color'] = '#2a2a2a';
                    }

                    return styleObj;
                }
            }
            return col;
        }).toArray();

    return spacers;
}


// Styles, Getters, Formatters etc..
function getHedgeColumnCellStyle(comp: HgHedgeMatrixComponent) {

    const f = function getCellStyle(params: CellClassParams) {

        const styleObj = {};
        styleObj['font-weight'] = 'normal';
        styleObj['background-color'] = 'rgba(0,0,0,0)';
        styleObj['color'] = undefined;
        styleObj['border-right-color'] = 'rgb(66, 66, 66)';

        const hId = getHedgeIdFromColId(params.colDef.colId);
        const hedgeData: HedgeData = hId ? comp.hedgeMatrixDataService.getHedge(hId) : undefined;

        if (isVoid(hedgeData)) {
            return styleObj;
        }

        if (params.data.isPinned) {
            if (params.data.isExpiration || params.data.isDayOfWeek) {
                styleObj['background-color'] = '#e8ded1';
                styleObj['color'] = '#2a2a2a';
                styleObj['font-weight'] = 'bold';
            } else if (params.data.isHedgePrice) {

                const value = comp.getHedgePriceWithTrans(hedgeData);

                styleObj['background-color'] = '#2a2a2a';

                if (value < 0) {
                    styleObj['color'] = 'red';
                } else if (value > 0) {
                    styleObj['color'] = 'green';
                }
            }
        } else {

            const strike = params.data?.strike;

            // Use getCellData (same path as valueGetter) — keyed by SPX-equivalent strike
            const cellData = comp.hedgeMatrixDataService.getCellData(strike, hedgeData?.id);

            if (isVoid(cellData)) {
                return styleObj;
            }

            const expirationCellDataIndex = getExpirationCellDataIndex(
                strike,
                params.api,
                params.node
            );

            const cell = cellData[expirationCellDataIndex];

            if (isVoid(cell) || !cell.qty) {
                return styleObj;
            }

            const probe = probeStrike(cell, comp);
            if (probe === 'unknown') {
                return styleObj;
            }

            styleObj['background-color'] = probe === 'missing'
                ? MissingStrikeBgColor
                : getColor(cell.qty);

            styleObj['color'] = 'white';

            // Per-expiration decision, whole-cell paint: a cell showing several expirations goes
            // stepping-coloured when ANY of them reverses (owner, 2026-08-24).
            const isStepping = cellData
                .some(x => comp.hedgeMatrixDataService
                    .stepsOnStrike(strike, x.expiration, x.optionType, x.underlying));

            if (isStepping) {
                styleObj['background-color'] = hedgeData.type === 'Call'
                    ? SteppingColourCall
                    : SteppingColourPut;
            }
        }

        return styleObj;
    };

    return f;

}

function getQtyTotalColumnCellStyle(comp: HgHedgeMatrixComponent, side: 'Call' | 'Put') {

    const f = function pricingColumnCellStyle(args: CellClassParams) {

        let styleObj = {};

        const rowData = args.data as HedgeMatrixRow;

        if (rowData && rowData.isPinned) {
            styleObj = {
                background: '#2a2a2a',
                color: 'white',
                'border-right-color': 'rgb(66, 66, 66)'
            };

            if (rowData.isPinned) {

                const val = args.value + '';
                const replaced = val.replace('$', '');
                const number = parseFloat(replaced);

                if (isValidNumber(number)) {
                    if (number < 0) {
                        styleObj['color'] = 'red';
                    } else if (number > 0) {
                        styleObj['color'] = 'green';
                    }
                }
            }

            return styleObj;
        }

        styleObj['font-weight'] = 'normal';
        styleObj['background'] = 'rgba(0,0,0,0)';
        styleObj['color'] = undefined;
        styleObj['border-right-color'] = 'rgb(66, 66, 66)';

        if (isVoid(args.value)) {
            return styleObj;
        }

        const strike = args.data.strike;

        const visible = comp.getSelectedHedgesIds(side);

        const qty = comp.hedgeMatrixDataService
            .getTotalQtyForStrike(strike, side, visible);

        if (!isValidNumber(qty, true)) {
            return styleObj;
        }

        const contributing = findContributingCell(comp, strike, side);
        if (!isVoid(contributing)) {
            const probe = probeStrike(contributing, comp);
            if (probe === 'unknown') {
                return styleObj;
            }
            styleObj['background-color'] = probe === 'missing'
                ? MissingStrikeBgColor
                : getColor(qty);
        } else {
            styleObj['background-color'] = getColor(qty);
        }

        styleObj['color'] = 'white';

        if (args.data.rowSpan > 1) {
            styleObj['display'] = 'flex';
            styleObj['align-items'] = 'center';
            styleObj['justify-content'] = 'center';
            styleObj['border-bottom-color'] = 'rgb(66, 66, 66)';
        }

        return styleObj;

    };

    return f;

}

function getColor(qty: number) {
    return qty > 0 ? '#00007c' : 'darkred';
}

const MissingStrikeBgColor = '#888888';
const QuoteWaitMs = 3000;
const FirstSeenIndex: Map<string, number> = new Map();
const PendingRefresh: Set<string> = new Set();

type StrikeProbe = 'present' | 'missing' | 'unknown';

function probeStrike(cell: HedgeMatrixCellData, comp: HgHedgeMatrixComponent): StrikeProbe {
    if (isVoid(cell)) {
        return 'unknown';
    }

    // Structural check — definitive miss when chain says strike not in expiration.
    const chain = comp.optionChainService.getCachedChain(cell.underlying);
    if (!isVoid(chain) && !isVoid(chain.expirations)) {
        const exp = chain.expirations.find(e => e.optionExpirationDate === cell.expiration);
        if (!isVoid(exp) && Array.isArray(exp.strikes)) {
            const target = isValidNumber(cell.actualStrike, true) ? cell.actualStrike : cell.strike;
            if (exp.strikes.indexOf(target) < 0) {
                return 'missing';
            }
        }
    }

    if (isVoid(cell.ticker)) {
        return 'unknown';
    }

    const q = comp.lastQuoteCache.getLastQuote(cell.ticker);
    if (!isVoid(q) && isValidNumber(q.mid, true)) {
        FirstSeenIndex.delete(cell.ticker);
        PendingRefresh.delete(cell.ticker);
        return 'present';
    }

    const now = Date.now();
    let firstSeen = FirstSeenIndex.get(cell.ticker);
    if (firstSeen === undefined) {
        firstSeen = now;
        FirstSeenIndex.set(cell.ticker, now);
    }
    const elapsed = now - firstSeen;
    if (elapsed >= QuoteWaitMs) {
        return 'missing';
    }

    if (!PendingRefresh.has(cell.ticker)) {
        PendingRefresh.add(cell.ticker);
        const ticker = cell.ticker;
        const remaining = QuoteWaitMs - elapsed + 50;
        setTimeout(() => {
            PendingRefresh.delete(ticker);
            comp.theGrid?.api?.refreshCells({force: true});
        }, remaining);
    }
    return 'unknown';
}

function findContributingCell(
    comp: HgHedgeMatrixComponent,
    strike: number,
    side: 'Call' | 'Put'
): HedgeMatrixCellData | undefined {
    const visibleHedges = comp.getSelectedHedgesIds(side);
    if (isVoid(visibleHedges) || visibleHedges.length === 0) {
        return undefined;
    }
    for (const hId of visibleHedges) {
        const cellData = comp.hedgeMatrixDataService.getCellData(strike, hId);
        if (isVoid(cellData)) {
            continue;
        }
        for (const cell of cellData) {
            if (isVoid(cell) || cell.optionType !== side) {
                continue;
            }
            const contribution = (cell.qty || 0) + (cell.transQty || 0);
            if (contribution !== 0) {
                return cell;
            }
        }
    }
    return undefined;
}

function getExpirationColumnCellStyle(comp: HgHedgeMatrixComponent, expiration: ExpirationDescriptor, emColumnId?: 'Expiration' | 'PnL') {

    const f = function pricingColumnCellStyle(args: CellClassParams) {

        const styleObj = {};

        styleObj['font-weight'] = 'normal';
        styleObj['background-color'] = 'rgba(0,0,0,0)';
        styleObj['color'] = undefined;
        styleObj['border-right-color'] = 'rgb(66, 66, 66)';


        if (args.data.isPinned) {
            if (args.data.isExpiration || args.data.isDayOfWeek) {
                styleObj['background-color'] = '#e8ded1';
                styleObj['color'] = '#2a2a2a';
                styleObj['font-weight'] = 'bold';
            } else if (args.data.isHedgePrice) {
                styleObj['background-color'] = '#2a2a2a';

                const val = args.value + '';
                const replaced = val.replace('$', '');
                const number = parseFloat(replaced);

                if (number < 0) {
                    styleObj['color'] = 'red';
                } else if (number > 0) {
                    styleObj['color'] = 'green';
                }
            }
        }

        if (args.data.rowSpan > 1) {
            styleObj['display'] = 'flex';
            styleObj['align-items'] = 'center';
            styleObj['justify-content'] = 'center';
            styleObj['border-bottom-color'] = 'rgb(66, 66, 66)';
            styleObj['background-color'] = '#2a2a2a';
        }

        const silo = parseColId(args.colDef.colId).silo;
        const underlying = expiration.underlyingTicker || silo;
        const nativeStrike = args.data.strike + (silo && silo !== comp.hedgeMatrixDataService.getPortfolioUnderlying()
            ? comp.hedgeMatrixDataService.getFairValueOffset(silo) : 0);
        const emsArgs: ExpectedMoveArgs = {
            strike: args.data.isPinned ? undefined : nativeStrike,
            source: 'Hedge Matrix',
            ticker: underlying,
            expiration: expiration.expiration,
            expirationTicker: expiration.expirationTicker,
            underlyingTicker: underlying,
            column: emColumnId
        };

        const rangeRegion = comp.expectedMoveService.getStrikeRegion(emsArgs);

        // Always reset the same property: ag-Grid merges styles during refresh.
        styleObj['background'] = styleObj['background-color'];
        if (rangeRegion) {
            styleObj['background'] = rangeRegion === 'near' ? 'grey' : '#55565b';
        }

        return styleObj;
    };

    return f;

}

function getModifierColumnCellStyle(comp: HgHedgeMatrixComponent, colType: 'outcomeQty' | 'transQty') {

    const f = function pricingColumnCellStyle(args: CellClassParams) {

        const styleObj = {};

        styleObj['font-weight'] = 'normal';
        styleObj['background-color'] = 'rgba(0,0,0,0)';
        styleObj['color'] = undefined;
        styleObj['border-right-color'] = 'rgb(66, 66, 66)';

        const hId = getHedgeIdFromColId(args.colDef.colId);
        const hedgeData: HedgeData = hId ? comp.hedgeMatrixDataService.getHedge(hId) : undefined;

        if (isVoid(hedgeData)) {
            return styleObj;
        }

        if (args.data.isPinned) {

            styleObj['background-color'] = '#2a2a2a';

            if (args.data.isHedgePrice) {

                const value = parseFloat(args.value);

                if (!isValidNumber(value)) {
                    return styleObj;
                }

                if (value < 0) {
                    styleObj['color'] = 'red';
                } else if (value > 0) {
                    styleObj['color'] = 'green';
                }

            } else if (args.data.isExpiration || args.data.isDayOfWeek) {
                styleObj['background-color'] = '#e8ded1';
                styleObj['color'] = '#2a2a2a';
                styleObj['font-weight'] = 'bold';
            }

            return styleObj;
        }

        const val = parseFloat(args.value);

        if (!isValidNumber(val, true)) {
            return styleObj;
        }

        const strike = args.data.strike;

        const cellData = comp
            .hedgeMatrixDataService
            .getCellData(strike, hedgeData.id);

        let cell: HedgeMatrixCellData | undefined;
        if (!isVoid(cellData)) {
            const expirationCellDataIndex = getExpirationCellDataIndex(
                strike,
                args.api,
                args.node
            );
            cell = cellData[expirationCellDataIndex];
        }

        if (!isVoid(cell)) {
            const probe = probeStrike(cell, comp);
            if (probe === 'unknown') {
                return styleObj;
            }
            styleObj['background-color'] = probe === 'missing'
                ? MissingStrikeBgColor
                : getColor(val);
        } else {
            styleObj['background-color'] = getColor(val);
        }

        styleObj['color'] = 'white';

        if (isVoid(cellData) || isVoid(cell)) {
            return styleObj;
        }

        // Per-expiration decision, whole-cell paint — see the sibling call in getHedgeColumnCellStyle.
        const isStepping = cellData
            .some(x => comp.hedgeMatrixDataService
                .stepsOnStrike(strike, x.expiration, x.optionType, x.underlying));

        if (isStepping) {
            styleObj['background-color'] = hedgeData.type === 'Call'
                ? SteppingColourCall
                : SteppingColourPut;
        }

        return styleObj;

    };
    return f;
}

export function getExpirationCellDataIndex(strike: number, gridApi: GridApi, selectedNode: RowNode) {
    let count = 0;
    let count2 = count;
    gridApi.forEachNode((node) => {
        const data = node.data as HedgeMatrixRow;

        if (isVoid(data)) {
            return;
        }

        if (data.strike !== strike) {
            return;
        }

        count++;

        if (node === selectedNode) {
            count2 = count - 1;
        }
    });
    return count2;
}

export function getHedgeExpirationsString(comp: HgHedgeMatrixComponent, hedge: HedgeData, column: 'original' | 'trans' | 'outcome'): string {
    let hedgeExpirations = comp.hedgeMatrixDataService
        .getHedgeExpirationsByColumn(hedge.id, column);

    if (isVoid(hedgeExpirations)) return null;

    if (hedgeExpirations.length === 1) return makeGuiFriendlyExpirationDate(hedgeExpirations[0]);

    const dtExpirations = hedgeExpirations.sort().map(x => DateTime.fromISO(x));

    const exp1 = dtExpirations[0];
    const exp2 = dtExpirations[1];

    const day1 = exp1.toFormat('dd');
    const day2 = exp2.toFormat('dd');

    const month1 = exp1.toFormat('MMM');
    const month2 = exp2.toFormat('MMM');

    const year1 = exp1.toFormat('yy');
    const year2 = exp2.toFormat('yy');

    const day = `${day1}/${day2}`;
    const month = month1 === month2 ? month1 : `${month1}/${month2}`;
    const year = year1 === year2 ? year2 : `${year1}/${year2}`;

    const header = [day, month, year].join('-');

    return header;
}

export function getDaysOfWeekString(comp: HgHedgeMatrixComponent, hedge: HedgeData, column: 'original' | 'trans' | 'outcome'): string {
    let hedgeExpirations = comp.hedgeMatrixDataService
        .getHedgeExpirationsByColumn(hedge.id, column);

    if (isVoid(hedgeExpirations)) return null;

    const cursorDate = comp.getCursorDate();

    if (hedgeExpirations.length === 1) return makeDayOfWeekDate(hedgeExpirations[0], false, cursorDate);

    const daysOfWeek = hedgeExpirations.sort()
        .map(x => makeDayOfWeekDate(x, false, cursorDate))
        .map(x => {
            const parts = x.split(' ');
            parts[1] = parts[1].replace('(', '');
            parts[1] = parts[1].replace(')', '');
            return parts;
        });

    const d1 = daysOfWeek[0];
    const d2 = daysOfWeek[1];

    const dows = `${d1[0]}/${d2[0]}`;
    const dtes = `${d1[1]}/${d2[1]}`;

    const result = `${dows} (${dtes})`;

    return result;
}

// Surgical multi-expiration row sync: derives which strikes need a rowspan-2 base + companion row
// from the CURRENT cell data and applies only the delta (no setRowData — the viewport never moves).
// Extracted from the qty value setter (CR-20260901-005) so the undo/redo restore path reuses the
// exact row logic every user edit already goes through. Returns true when rows were added/removed.
export function syncCompanionRows(comp: HgHedgeMatrixComponent, api: GridApi): boolean {
    const hedges = comp.hedgeMatrixDataService.getHedges();
    const doubleStrikes = new Set<number>();
    hedges.forEach(h => {
        const cellData = comp.hedgeMatrixDataService.getCellDataForHedge(h.id);
        const groups: Record<number, number> = {};
        (cellData || []).forEach(cd => {
            groups[cd.strike] = (groups[cd.strike] || 0) + 1;
        });
        Object.keys(groups).forEach(s => {
            if (groups[parseInt(s, 10)] > 1) doubleStrikes.add(parseInt(s, 10));
        });
    });

    const toAdd: HedgeMatrixRow[] = [];
    const toRemove: HedgeMatrixRow[] = [];

    api.forEachNode(n => {
        const row = n.data as HedgeMatrixRow;
        if (row.isPinned || row.strike == null) return;
        const isCompanion = row.rowId && row.rowId.includes('-');
        const shouldSpan = doubleStrikes.has(row.strike);

        if (isCompanion && !shouldSpan) {
            toRemove.push(row);
        } else if (!isCompanion) {
            if (shouldSpan && row.rowSpan !== 2) {
                row.rowSpan = 2;
                row.isMultiExpiration = true;
                // Check if companion already exists
                const companionId = row.strike + '-1';
                let hasCompanion = false;
                api.forEachNode(cn => {
                    if (cn.data.rowId === companionId) hasCompanion = true;
                });
                if (!hasCompanion) {
                    toAdd.push({
                        rowId: companionId,
                        strike: row.strike,
                        rowSpan: 1,
                        isMultiExpiration: true
                    });
                }
            } else if (!shouldSpan && row.rowSpan === 2) {
                row.rowSpan = 1;
                row.isMultiExpiration = false;
            }
        }
    });

    const didUpdateRows = toAdd.length > 0 || toRemove.length > 0;

    if (didUpdateRows) {
        api.applyTransaction({ add: toAdd, remove: toRemove });
        const baseSilo = comp.hedgeMatrixDataService.getPortfolioUnderlying();
        api.setSortModel([
            { colId: buildStrikeColId(baseSilo), sort: 'desc' }
        ]);
        // Force ag-Grid to re-read rowSpan on mutated rows
        api.redrawRows();
    }

    return didUpdateRows;
}

function getQtyValueSetter(comp: HgHedgeMatrixComponent, hedge: HedgeData, field: keyof HedgeMatrixCellData): (params: ValueSetterParams) => boolean {
    function valueSetter(params: ValueSetterParams) {
        const strike = params.data.strike;

        if (!isValidNumber(strike, true)) {
            return null;
        }

        const beforeCellData = comp.hedgeMatrixDataService.getCellData(
            strike,
            hedge.id
        );




        let beforeExpiration: string;

        if (!isVoid(beforeCellData)) {

            const expirationCellDataIndex = getExpirationCellDataIndex(
                strike,
                params.api,
                params.node
            );

            const expirationCellData = beforeCellData[expirationCellDataIndex];

            beforeExpiration = expirationCellData?.expiration;
        }

        const qtyObj = params.newValue;

        let expiration: string;
        let qtyToSet: number;
        let addNewLeg = false;

        if (typeof qtyObj === 'object') {
            if (qtyObj.isCancel) {
                return false;
            }
            qtyToSet = qtyObj.qty;
            expiration = qtyObj.expiration;
            addNewLeg = qtyObj.addNewLeg;

        } else {

            expiration = beforeExpiration;

            if (isVoid(expiration)) {

                expiration = comp.hedgeMatrixDataService.getHedgeDefaultExpiration(hedge.id);

                const column = field === 'transQty' ? 'trans' : 'outcome';
                const expirationsByColumn = comp.hedgeMatrixDataService.getHedgeExpirationsByColumn(hedge.id, column);

                if (expirationsByColumn.length === 1) {
                    expiration = expirationsByColumn[0];
                }

                if (isVoid(expiration)) {
                    const nearestExpiration = comp.hedgeMatrixDataService.getNearestExpiration();
                    expiration = nearestExpiration.optionExpirationDate;
                }
            }

            qtyToSet = parseInt(params.newValue);
        }

        if (!isValidNumber(qtyToSet, true)) {
            qtyToSet = null;
        }

        // CoPilot (CR-20260619-001): a genuine USER cell commit reached here (cancel + invalid-strike
        // already returned above). CoPilot's own preview writes call the data service directly and never
        // enter this setter, so this is a clean user-only divergence signal.
        comp.noteUserMatrixEdit(hedge.id);

        // CR-20260901-005: BEGIN fallback for commits with no edit session (e.g. clipboard paste
        // into a cell) — a session's own BEGIN from cellEditingStarted is kept. END is at the
        // bottom of this setter, after the mutations.
        comp.ensureMatrixUndoTransaction();

        if (field === 'transQty') {

            if (!addNewLeg) {
                comp.hedgeMatrixDataService.setTransQty(
                    hedge,
                    strike,
                    beforeExpiration,
                    null
                );

            }

            comp.hedgeMatrixDataService.setTransQty(
                hedge,
                strike,
                expiration,
                qtyToSet
            );

            if (hedge.isNew) {
                const cellData = comp.hedgeMatrixDataService.getCellData(
                    strike,
                    hedge.id
                );

                if (isVoid(cellData)) {
                    if (!isVoid(expiration)) {
                        hedge.initialExpiration = expiration;
                    }
                }
            }
        } else if (field === 'outcomeQty') {

            comp.hedgeMatrixDataService.setOutcomeQty(
                hedge,
                strike,
                expiration,
                qtyToSet
            );

            if (beforeExpiration !== expiration) {
                if (!addNewLeg) {
                    comp.hedgeMatrixDataService.setOutcomeQty(
                        hedge,
                        strike,
                        beforeExpiration,
                        null
                    );
                }
            }

        } else {
            throw new Error(`unsupported field: ${field}`);
        }



        // Check ALL strikes for multi-row changes (roll may affect multiple strikes)
        const didUpdateRows = syncCompanionRows(comp, params.api);

        if (!didUpdateRows) {
            comp.fillReferenceLists();
            setTimeout(() => comp.onChange());
        }

        setTimeout(() => {
            comp.fillReferenceLists();
            comp.recalculatePnls();
            params.api.refreshCells({force: true});
        });

        // CR-20260901-005: END — every mutation of this commit (punch-pad side effects included,
        // they ran before this setter) is now in place; diff-and-push the transaction.
        comp.endMatrixUndoTransaction();

        return true;
    }

    return valueSetter;
}

function getValueFormatterForHedgeColumn(comp: HgHedgeMatrixComponent, hedge: HedgeData): (params: ValueFormatterParams) => string {
    function valueFormatter(params: ValueFormatterParams) {
        const value = params.value;

        if (!isValidNumber(value)) {
            return null;
        }

        const strike = params.data.strike;

        if (!isValidNumber(strike, true)) {
            if (params.data && params.data.isPinned) {
                if (params.data.isHedgePrice) {
                    return defaultCurrencyFormatter(value);
                }
            }
            return value;
        }

        const cellData = comp.hedgeMatrixDataService.getCellData(
            strike,
            hedge.id
        );

        if (isVoid(cellData)) {
            return value;
        }


        const expirationCellDataIndex = getExpirationCellDataIndex(
            strike,
            params.api,
            params.node
        );

        const expirationCellData = cellData[expirationCellDataIndex];

        if (isVoid(expirationCellData)) {
            return value;
        }

        let ticker = expirationCellData.ticker;

        if (isVoid(ticker)) {
            ticker = comp.hedgeMatrixDataService.getTickerForCell(
                strike,
                expirationCellData.expiration,
                expirationCellData.optionType
            );

            if (isVoid(ticker)) {
                return value;
            }
            expirationCellData.ticker = ticker;
        }

        const isExpired = comp.optionChainService.isOptionExpired(ticker);

        comp.lastQuoteCache.subscribeIfNotYet(ticker);

        const lastQuote = comp.lastQuoteCache.getLastQuote(ticker);

        if (!isValidNumber(lastQuote?.mid, true)) {
            if (!isExpired) {
                return value;
            }
        }

        let sign = '';

        if (value > 0) {
            sign = '+';
        }


        const quote = isExpired ? undefined : defaultCurrencyFormatter(lastQuote.mid);

        let formatValue = isExpired ? `EXP (${sign}${value}) ` : `${quote} (${sign}${value}) `;

        const hasMultipleExpiration = comp.hedgeMatrixDataService.doesHaveMultipleExpirations(
            expirationCellData.hedgeId
        );

        if (hasMultipleExpiration) {
            const dow = makeDayOfWeekDate(expirationCellData.expiration);
            formatValue += `, ${dow}`;
        }

        return formatValue;


    }

    return valueFormatter;
}


// ──────────────────────────────────────────────────────
// ES (Non-Base Underlying) Section Columns
// ──────────────────────────────────────────────────────

interface EsUnderlyingSection {
    underlying: string;
    displayName: string;
    hedgeList: { id: string, label: string, side: 'Call' | 'Put' }[];
    expirationList: ExpirationDescriptor[];
    pnlExpirationsList: ExpirationDescriptor[];
}

/**
 * ES Strike column wrapped in a section group. Extracted for reuse by the state-driven factory.
 */
export function buildEsStrikeGroup(comp: HgHedgeMatrixComponent, section: EsUnderlyingSection): ColGroupDef {
    const ul = section.underlying;
    const ulDisplay = section.displayName;
    const fairValue = comp.hedgeMatrixDataService.getFairValueOffset(ul);

    const esStrikeCol: ColDef = {
        headerName: 'Strike',
        colId: buildStrikeColId(ul),
        minWidth: width80,
        width: width80,
        headerClass: 'ets-text-centered',
        sortable: false,
        cellStyle(args: CellClassParams) {
            if (!args.data) return undefined;

            if (args.data.isPinned) {
                const style = {
                    background: '#2a2a2a',
                    color: 'gold',
                    'font-weight': 'bold',
                };
                if (isValidNumber(args.value, true)) {
                    if (args.value < 0) style['color'] = 'red';
                    else if (args.value > 0) style['color'] = 'green';
                }
                return style;
            }

            const styleObj = {
                color: '#fff',
                'font-weight': '800',
                background: 'inherit',
                border: 'none',
            };

            const atmStrike = comp.getAtmStrike();
            const esAtmStrike = atmStrike + fairValue;
            const esStrike = args.data.strike + fairValue;

            if (esStrike === esAtmStrike) {
                styleObj['background'] = '#fff';
                styleObj['color'] = 'black';
            }

            const rowData = args.data as HedgeMatrixRow;
            if (rowData.rowSpan > 1) {
                styleObj['display'] = 'flex';
                styleObj['align-items'] = 'center';
                styleObj['justify-content'] = 'center';
                styleObj['border-bottom-color'] = 'rgb(66, 66, 66)';
            }

            return styleObj;
        },
        valueGetter: (params: ValueGetterParams) => {
            const rowData = params.data as HedgeMatrixRow;
            if (!rowData || rowData.isPinned) {
                if (!rowData) return null;
                const liveEsHedgeIds = comp.hedgeMatrixDataService.getHedgesByUnderlying(ul)
                    .map(h => h.id)
                    .filter(id => comp.isHedgeSelected({id}));
                const totalOriginalCost = comp.hedgeMatrixDataService.getTotalOriginalCost(undefined, liveEsHedgeIds);
                const totalTransCost = comp.hedgeMatrixDataService.getTotalTransCost(undefined, liveEsHedgeIds);
                if (rowData.isExpiration) {
                    return isValidNumber(totalTransCost) ? totalOriginalCost : null;
                } else if (rowData.isDayOfWeek) {
                    return totalTransCost;
                }
                return null;
            }
            return rowData.strike + fairValue;
        },
        valueFormatter: (params: ValueFormatterParams) => {
            const rowData = params.data as HedgeMatrixRow;
            if (rowData?.isPinned) {
                return isValidNumber(params.value, true) ? defaultCurrencyFormatter(params.value) : null;
            }
            return undefined;
        },
    };

    return {
        headerName: ulDisplay,
        groupId: buildSectionGroupId(ul),
        marryChildren: false,
        children: [esStrikeCol]
    };
}

/**
 * Generates the complete column group for one non-base underlying (e.g., ES contract).
 * Contains: ES Strike column, ES hedge columns, ES total columns, ES P&L columns.
 * All values are normalized to SPX-equivalent via the normalization ratio.
 */
function getEsUnderlyingSectionColumns(
    comp: HgHedgeMatrixComponent,
    section: EsUnderlyingSection
): Partial<AgGridColumn>[] {

    const cols: Partial<AgGridColumn>[] = [];
    const ul = section.underlying;
    const ulDisplay = section.displayName;

    cols.push(buildEsStrikeGroup(comp, section));

    // ES expiration pricing columns (hidden by default, toggled via selector)
    // Deduplicate by expiration date — getEsPricingColumns already creates both Call and Put
    const esUniqueExpDates = section.expirationList
        .map(x => x.expiration)
        .filter((v, i, a) => a.indexOf(v) === i);
    const esExpirations = esUniqueExpDates.flatMap(exp => {
        const expDesc = section.expirationList.find(x => x.expiration === exp) as ExpirationDescriptor;
        return getEsPricingColumns(comp, expDesc, ul, ulDisplay);
    });
    cols.push(...esExpirations);

    // ES hedge columns — all hedges in data-service order
    const esHedges = comp.hedgeMatrixDataService.getHedgesByUnderlying(ul);
    const esHedgeCols = esHedges.map(h => getHedgeColumn(comp, h));
    cols.push(...esHedgeCols);

    // ES P&L per (side, expiration)
    const esPnlExpirations = section.pnlExpirationsList.flatMap(x => {
        const col = getEsPnlExpirationColumn(comp, x, ul, section, false, ulDisplay);
        const allInclusive = getEsPnlExpirationColumn(comp, x, ul, section, true, ulDisplay);
        return [col, allInclusive];
    });
    cols.push(...esPnlExpirations);

    // ES Grand Total P&L columns
    const esUniqueExps = Enumerable.from(section.expirationList)
        .select(x => x.expiration).distinct().toArray();
    const esGrandTotalCols = esUniqueExps.map(exp => {
        const expDesc = section.expirationList.find(y => y.expiration === exp);
        return getEsPnlTotalColumn(comp, expDesc, ul, section, ulDisplay);
    });
    cols.push(...esGrandTotalCols);

    // ES total columns (simplified — scoped to ES hedges only)
    const esTotalCalls = getEsQtyTotalColumn(comp, 'Call', section, ulDisplay);
    const esTotalPuts = getEsQtyTotalColumn(comp, 'Put', section, ulDisplay);
    if (esTotalCalls) cols.push(esTotalCalls);
    if (esTotalPuts) cols.push(esTotalPuts);

    return cols;
}

/**
 * Simplified total column scoped to hedges of a specific non-base underlying.
 */
export function getEsQtyTotalColumn(
    comp: HgHedgeMatrixComponent,
    side: 'Call' | 'Put',
    section: EsUnderlyingSection,
    underlyingDisplay?: string
): ColGroupDef {

    const ul = section.underlying;
    const ulDisplay = section.displayName;

    const headerText = `TOTAL ${side.toUpperCase()}S`;
    const earmark = makeEarmarkedHeaderTemplate(headerText, underlyingDisplay);

    const totalColumn: ColDef = {
        headerName: headerText,
        ...(earmark ? { headerComponentParams: earmark } : {}),
        colId: buildQtyTotalColId(ul, side),
        width: width90 + earmarkWidthExtra,
        minWidth: width90 + earmarkWidthExtra,
        sortable: false,
        cellStyle: (args: CellClassParams) => {
            const styleObj = {};
            styleObj['font-weight'] = 'normal';
            styleObj['background-color'] = 'rgba(0,0,0,0)';
            styleObj['color'] = undefined;
            styleObj['border-right-color'] = 'rgb(66, 66, 66)';

            const rowData = args.data as HedgeMatrixRow;

            if (rowData?.isPinned) {
                styleObj['background'] = '#2a2a2a';
                styleObj['color'] = 'white';

                const val = args.value + '';
                const replaced = val.replace('$', '');
                const number = parseFloat(replaced);

                if (isValidNumber(number)) {
                    if (number < 0) {
                        styleObj['color'] = 'red';
                    } else if (number > 0) {
                        styleObj['color'] = 'green';
                    }
                }

                return styleObj;
            }

            if (!isValidNumber(args.value, true)) {
                return styleObj;
            }

            const qty = args.value;
            styleObj['background-color'] = qty > 0 ? '#00007c' : 'darkred';
            styleObj['color'] = 'white';

            return styleObj;
        },
        valueGetter: (params: ValueGetterParams) => {
            const rowData = params.data as HedgeMatrixRow;
            if (!rowData) return null;

            if (rowData.isPinned) {
                const liveIds = comp.hedgeMatrixDataService.getHedgesByUnderlying(ul)
                    .filter(h => h.type === side)
                    .map(h => h.id)
                    .filter(id => comp.isHedgeSelected({id}));
                const totalOriginalCost = comp.hedgeMatrixDataService.getTotalOriginalCost(side, liveIds);
                const totalTransCost = comp.hedgeMatrixDataService.getTotalTransCost(side, liveIds);

                if (rowData.isExpiration) {
                    return isValidNumber(totalTransCost) ? totalOriginalCost : null;
                } else if (rowData.isDayOfWeek) {
                    return totalTransCost;
                }
                return null;
            }

            const liveIds = comp.hedgeMatrixDataService.getHedgesByUnderlying(ul)
                .filter(h => h.type === side)
                .map(h => h.id)
                .filter(id => comp.isHedgeSelected({id}));
            const strike = rowData.strike;
            const total = comp.hedgeMatrixDataService.getTotalQtyForStrike(strike, side, liveIds);
            return isValidNumber(total, true) ? total : null;
        },
        valueFormatter: (params: ValueFormatterParams) => {
            const value = params.value;
            if (!isValidNumber(value, true)) return null;

            const rowData = params.data as HedgeMatrixRow;
            if (rowData?.isPinned) {
                return defaultCurrencyFormatter(value);
            }

            // Look up price for this strike (same logic as SPX total, adjusted for ES fair value)
            let ticker;
            const liveIds = comp.hedgeMatrixDataService.getHedgesByUnderlying(ul).filter(h => h.type === side).map(h => h.id);
            const hedgeDatum = comp.hedgeMatrixDataService.getHedges()
                .filter(x => liveIds.indexOf(x.id) >= 0 && x.type === side && !isVoid(x.legs))[0];

            if (hedgeDatum) {
                const hedgePosition = Enumerable.from(hedgeDatum.legs)
                    .firstOrDefault(x => !isVoid(x.ticker));

                if (!isVoid(hedgePosition)) {
                    const optionTicker = parseOptionTicker(hedgePosition.ticker);
                    const fairValue = comp.hedgeMatrixDataService.getFairValueOffset(ul);
                    optionTicker.strike = rowData.strike + fairValue;
                    ticker = zipOptionTicker(optionTicker);
                }
            }

            const lastQuote = comp.lastQuoteCache.getLastQuote(ticker);

            if (isVoid(lastQuote)) {
                return `${value > 0 ? '+' : ''}${value}`;
            }

            const sign = value > 0 ? '+' : '';
            const quote = defaultCurrencyFormatter(lastQuote.mid);
            return `${quote} (${sign}${value}) `;
        }
    };

    const allEsHedgeIds = section.hedgeList.map(h => h.id);

    const grp: ColGroupDef = {
        headerGroupComponent: 'matrixTotalGroupHeader',
        headerGroupComponentParams: {
            context: {
                comp,
                type: 'side',
                side: side,
                hedgeIds: allEsHedgeIds,
            },
        },
        groupId: buildQtyTotalGroupId(ul, side),
        children: [totalColumn]
    };

    return grp;
}

/**
 * Single ES expiration pricing column for one side.
 */
function getEsPricingColumn(
    comp: HgHedgeMatrixComponent,
    expiration: ExpirationDescriptor,
    ul: string,
    side: 'Call' | 'Put',
    underlyingDisplay?: string
): ColDef {
    const fairValue = comp.hedgeMatrixDataService.getFairValueOffset(ul);
    const headerText = `${side.toUpperCase()}S`;
    const earmark = makeEarmarkedHeaderTemplate(headerText, underlyingDisplay);
    return {
        headerName: headerText,
        ...(earmark ? { headerComponentParams: earmark } : {}),
        colId: buildPricingColId(ul, expiration.expiration, side),
        minWidth: width90,
        maxWidth: width90,
        headerClass: 'ets-text-centered',
        hide: true,
        sortable: false,
        rowSpan: (params: RowSpanParams) => params.data.rowSpan || 1,
        valueGetter: (params: ValueGetterParams) => {
            if (params.data.isPinned) {
                if (params.data.isExpiration || params.data.isDayOfWeek) {
                    return params.data.isExpiration
                        ? expiration.expirationFriendly
                        : expiration.dayOfWeek;
                }
                return undefined;
            }
            const esStrike = params.data.strike + fairValue;
            const ticker = comp.hedgeMatrixDataService.getTickerForCell(
                esStrike, expiration.expiration, side, ul
            );
            if (!ticker) return null;
            const lastQuote = comp.lastQuoteCache.getLastQuote(ticker);
            return lastQuote?.mid;
        },
        valueFormatter: (params: ValueFormatterParams) => {
            if (!isValidNumber(params.value, true)) return null;
            return defaultCurrencyFormatter(params.value);
        },
        cellStyle: getExpirationColumnCellStyle(comp, expiration, 'Expiration')
    };
}

/**
 * ES expiration pricing columns — both sides. Delegates to getEsPricingColumn.
 */
function getEsPricingColumns(
    comp: HgHedgeMatrixComponent,
    expiration: ExpirationDescriptor,
    ul: string,
    underlyingDisplay?: string
): ColDef[] {
    return [
        getEsPricingColumn(comp, expiration, ul, 'Call', underlyingDisplay),
        getEsPricingColumn(comp, expiration, ul, 'Put', underlyingDisplay)
    ];
}

/**
 * ES P&L expiration column — mirrors getPnlExpirationColumn() but with ES-prefixed colId.
 */
function getEsPnlExpirationColumn(
    comp: HgHedgeMatrixComponent,
    expiration: ExpirationDescriptor,
    ul: string,
    section: EsUnderlyingSection,
    allInclusive: boolean,
    underlyingDisplay?: string
): ColDef {
    const header = allInclusive
        ? `P&L ${expiration.side.toUpperCase()}S (All)`
        : `P&L ${expiration.side.toUpperCase()}S`;

    const colId = buildPnlExpirationColId(ul, expiration.expiration, expiration.side, allInclusive);

    const earmark = makeEarmarkedHeaderTemplate(header, underlyingDisplay);

    const col: ColDef = {
        headerName: header,
        ...(earmark ? { headerComponentParams: earmark } : {}),
        colId: colId,
        minWidth: width80 + earmarkWidthExtra,
        width: width80 + earmarkWidthExtra,
        headerClass: 'ets-text-centered',
        sortable: false,
        hide: true,
        rowSpan: (params: RowSpanParams) => params.data.rowSpan || 1,
        valueGetter: (params: ValueGetterParams) => {
            if (params.data.isPinned) {
                if (params.data.isExpiration || params.data.isDayOfWeek) {
                    return params.data.isExpiration
                        ? expiration.expirationFriendly
                        : expiration.dayOfWeek;
                }
                return undefined;
            }

            const liveEsHedgeIds = comp.hedgeMatrixDataService.getHedgesByUnderlying(ul).map(h => h.id);
            const visible = allInclusive ? liveEsHedgeIds : liveEsHedgeIds.filter(
                (id: string) => comp.isHedgeSelected({id})
            );

            const pnl = comp.hedgeMatrixDataService.getExpirationPnl(
                expiration.expiration,
                params.data.strike,
                expiration.side,
                visible
            );
            return pnl;
        },
        valueFormatter: (params: ValueFormatterParams) => {
            if (!isValidNumber(params.value, true)) return null;
            if (params.data.isPinned) {
                return defaultCurrencyFormatter(params.value);
            }
            return formatCurrency(params.value, 'en-US', '$', 'USD', '1.0-0');
        },
        cellStyle: getExpirationColumnCellStyle(comp, expiration, 'PnL')
    };

    return col;
}

/**
 * ES Grand Total P&L column — mirrors getPnlTotalColumn() but with ES-prefixed colId.
 */
function getEsPnlTotalColumn(
    comp: HgHedgeMatrixComponent,
    expiration: ExpirationDescriptor,
    ul: string,
    section: EsUnderlyingSection,
    underlyingDisplay?: string
): ColDef {
    const colId = buildPnlTotalColId(ul, expiration.expiration);

    const earmark = makeEarmarkedHeaderTemplate('P&L TOTAL', underlyingDisplay);

    const col: ColDef = {
        headerName: `P&L TOTAL`,
        ...(earmark ? { headerComponentParams: earmark } : {}),
        colId: colId,
        minWidth: width80 + earmarkWidthExtra,
        width: width80 + earmarkWidthExtra,
        headerClass: 'ets-ag-header-center',
        hide: true,
        sortable: false,
        rowSpan: (params: RowSpanParams) => params.data.rowSpan || 1,
        valueGetter: (params: ValueGetterParams) => {
            if (params.data.isPinned) {
                if (params.data.isExpiration || params.data.isDayOfWeek) {
                    return params.data.isExpiration
                        ? expiration.expirationFriendly
                        : expiration.dayOfWeek;
                }
                return undefined;
            }

            const liveEsHedgeIds = comp.hedgeMatrixDataService.getHedgesByUnderlying(ul).map(h => h.id);
            const visible = liveEsHedgeIds.filter((id: string) => comp.isHedgeSelected({id}));

            const callPnl = comp.hedgeMatrixDataService.getExpirationPnl(
                expiration.expiration, params.data.strike, 'Call', visible
            );
            const putPnl = comp.hedgeMatrixDataService.getExpirationPnl(
                expiration.expiration, params.data.strike, 'Put', visible
            );

            return callPnl + putPnl;
        },
        valueFormatter: (params: ValueFormatterParams) => {
            if (!isValidNumber(params.value, true)) return null;
            if (params.data.isPinned) {
                return defaultCurrencyFormatter(params.value);
            }
            return formatCurrency(params.value, 'en-US', '$', 'USD', '1.0-0');
        },
        cellStyle: getExpirationColumnCellStyle(comp, expiration, 'PnL')
    };

    return col;
}
